import { fitBuilding, restoreBuildingScale } from './fitBuilding';
import { metricRackSize, projectMetrics, PROJECT_UNITS_PER_METRE } from './projectMetrics';
import * as THREE from 'three';
import type { RefObject } from 'react';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader';
import { MeshoptDecoder } from 'three/examples/jsm/libs/meshopt_decoder.module.js';
import { SolarTracker, getPanelLayout } from './tracker/SolarTracker';
import { Sun } from './Sun';
import { Moon } from './Moon';
import { PanelInfo } from './PanelInfo';
import type { SolarTrackerPanel } from './tracker/types/SolarTrackerPanel';


export type TrackerRef = RefObject<
  (THREE.Object3D & { energyHistory?: number[]; type?: string; name?: string }) | null
>;



export type SetupSceneProps = {
  mountRef: RefObject<HTMLElement | null>;
  sunLightRef: RefObject<THREE.DirectionalLight | null>;
  sunSphereRef: RefObject<THREE.Mesh | null>;
  moonSphereRef?: RefObject<THREE.Mesh | null>;
  controlsRef: RefObject<OrbitControls | null>;
  updateSunPosition: () => void;
  trackerMap: Map<string, SolarTrackerPanel>;
  panelInfoMap: Map<string, PanelInfo>;
  renderPausedRef?: RefObject<boolean>;
  wallMeshRef?: RefObject<THREE.Mesh | null>;
  onAssetProgress?: (loaded: number, total: number) => void;
};

export const setupScene = ({
  mountRef,
  sunLightRef,
  sunSphereRef,
  moonSphereRef,
  controlsRef,
  updateSunPosition,
  trackerMap,
  panelInfoMap,
  renderPausedRef,
  wallMeshRef,
  onAssetProgress,
}: SetupSceneProps) => {

  const isProject=new URLSearchParams(window.location.search).has('project');

  // === Neighborhood-Asset-Fortschritt (Häuser/See/Yacht) fürs Boot-Log im Splash
  const ASSET_TOTAL = 5;
  const HOUSES_FOR_FLYTHROUGH = 2; // beide Häuser geladen -> Ambient-Loop darf lospreschen
  let assetLoaded = 0;
  let housesLoaded = 0;
  const reportAssetLoaded = (isHouse = false) => {
    assetLoaded += 1;
    onAssetProgress?.(assetLoaded, ASSET_TOTAL);
    if (isHouse) housesLoaded += 1;
    if (housesLoaded >= HOUSES_FOR_FLYTHROUGH && ambientAutoArmed && !introActive) {
      startAmbientLoop();
    }
  };


  // === Container-Größe
  const getSize = () => {
    const el = mountRef.current as HTMLElement | null;
    const w = el?.clientWidth ?? window.innerWidth;
    const h = el?.clientHeight ?? window.innerHeight;
    return { w: Math.max(1, w), h: Math.max(1, h) };
  };
  const { w: initW, h: initH } = getSize();

  // === Szene
  const scene = new THREE.Scene();
  const textureLoader = new THREE.TextureLoader();
  let disposed = false;
  const disposedGeometries = new Set<THREE.BufferGeometry>();
  const disposedMaterials = new Set<THREE.Material>();
  const disposedTextures = new Set<THREE.Texture>();

  const disposeTexture = (texture?: THREE.Texture | null) => {
    if (!texture || disposedTextures.has(texture)) return;
    disposedTextures.add(texture);
    texture.dispose();
  };

  const disposeMaterial = (material?: THREE.Material | THREE.Material[] | null) => {
    const materials = Array.isArray(material) ? material : [material];
    materials.forEach((mat) => {
      if (!mat || disposedMaterials.has(mat)) return;
      Object.values(mat as any).forEach((value) => {
        if ((value as any)?.isTexture) disposeTexture(value as THREE.Texture);
      });
      disposedMaterials.add(mat);
      mat.dispose();
    });
  };

  const disposeObjectTree = (root: THREE.Object3D) => {
    root.traverse((obj: any) => {
      if (obj.geometry && !disposedGeometries.has(obj.geometry)) {
        disposedGeometries.add(obj.geometry);
        obj.geometry.dispose();
      }
      if (obj.material) disposeMaterial(obj.material);
    });
  };

  const disposePanelInfos = () => {
    panelInfoMap.forEach((panelInfo) => panelInfo.dispose());
    panelInfoMap.clear();
  };

  // --- MODE & GRUPPEN ---
  type HouseMode = 'multi' | 'single';
  let houseMode: HouseMode = 'multi';

  const neighborhoodGroup = new THREE.Group(); // viele Häuser + Umgebung
  neighborhoodGroup.name = 'Neighborhood';
  scene.add(neighborhoodGroup);

  const singleGroup = new THREE.Group(); // kleines Haus + neue Panels
  singleGroup.name = 'SingleSet';
  singleGroup.visible = false;
  scene.add(singleGroup);

  let singleHouse: THREE.Group | null = null;

  // === Kamera
  const camera = new THREE.PerspectiveCamera(75, initW / initH, 0.1, 100000);
  camera.position.set(0, 200, 500);
  scene.add(camera);

  // === Renderer
  // Touch-Geräte (Handys/Tablets) haben enge GPU-/Speicher-Budgets — iOS Safari
  // killt den Tab hart ("wiederholt ein Problem aufgetreten"), wenn er zu viel
  // Speicher zieht. Deshalb dort: kein MSAA, DPR-Deckel 1.5, Schatten aus (s.u.).
  const IS_COARSE_POINTER = window.matchMedia?.('(pointer: coarse)').matches ?? false;
  // powerPreference: Handys/Laptops mit zwei GPUs sollen die schnelle nehmen.
  const renderer = new THREE.WebGLRenderer({ antialias: !IS_COARSE_POINTER, powerPreference: 'high-performance' });
  // Retina-Displays (iPhone: DPR 3) rendern sonst auf 1x und sehen matschig aus.
  // Mobiler Deckel 1.5 statt 2: guter Schärfe-Kompromiss bei halbem Speicher.
  renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, IS_COARSE_POINTER ? 1.5 : 2));
  renderer.setSize(initW, initH, false);
  renderer.domElement.style.width = '100%';
  renderer.domElement.style.height = '100%';
  // Touch-Gesten gehören der 3D-Kamera (OrbitControls), nicht dem Browser
  // (kein Scroll/Pinch-Zoom der Seite auf dem Canvas).
  renderer.domElement.style.touchAction = 'none';
  // Schatten kosten einen zweiten Geometrie-Pass + 2048²-Shadow-Map — auf
  // Touch-Geräten deaktiviert (Speicher/Performance), am Desktop wie gehabt.
  renderer.shadowMap.enabled = !IS_COARSE_POINTER;
  renderer.shadowMap.type = THREE.PCFSoftShadowMap;
  // Ohne Tone-Mapping clippt three.js jeden Lichtwert >1.0 hart auf reines Weiß statt
  // sanft auszubrennen — bei Ambient(0.6)+Ambient(0.15)+Hemisphere(0.7)+Sonne(1.0) reicht
  // das locker, um helle/weiße PBR-Flächen komplett flach und detaillos weiß zu rendern.
  // ACES-Filmic gibt Lichtern eine weiche Schulter statt eines harten Clips.
  renderer.toneMapping = THREE.ACESFilmicToneMapping;
  renderer.toneMappingExposure = 1.0;
  renderer.outputColorSpace = THREE.SRGBColorSpace;

  if (mountRef.current?.firstChild) {
    mountRef.current.removeChild(mountRef.current.firstChild as any);
  }
  mountRef.current?.appendChild(renderer.domElement);

  // === OrbitControls
  const controls = new OrbitControls(camera, renderer.domElement);
  controls.enableDamping = true;
  controlsRef.current = controls;

  // ============== SKY / AURORA / STARS / GALAXY ==============
  const skyClock = new THREE.Clock();
  type SkyPreset = 'day' | 'aurora' | 'night' | 'galaxy';

  const skyUniforms = {
    topColor: { value: new THREE.Color('#79b8ff') },
    bottomColor: { value: new THREE.Color('#dff3ff') },
    exponent: { value: 1.2 },
  };

  const skyDome = new THREE.Mesh(
    new THREE.SphereGeometry(6000, 32, 16),
    new THREE.ShaderMaterial({
      uniforms: skyUniforms,
      vertexShader: `
        varying vec3 vWorldPosition;
        void main() {
          vec4 wp = modelMatrix * vec4(position,1.0);
          vWorldPosition = wp.xyz;
          gl_Position = projectionMatrix * viewMatrix * wp;
        }`,
      fragmentShader: `
        uniform vec3 topColor;
        uniform vec3 bottomColor;
        uniform float exponent;
        varying vec3 vWorldPosition;
        void main() {
          float h = normalize(vWorldPosition).y * 0.5 + 0.5;
          float f = pow(max(h,0.0), exponent);
          gl_FragColor = vec4(mix(bottomColor, topColor, f), 1.0);
        }`,
      side: THREE.BackSide,
      depthWrite: false,
    })
  );
  skyDome.renderOrder = -1000;
  scene.add(skyDome);

  const auroraUniforms = {
    time: { value: 0 },
    intensity: { value: 0.5 },
    color1: { value: new THREE.Color('#34d399') },
    color2: { value: new THREE.Color('#60a5fa') },
  };

  const auroraMat = new THREE.ShaderMaterial({
    uniforms: auroraUniforms,
    transparent: true,
    depthTest: false,
    depthWrite: false,
    blending: THREE.AdditiveBlending,
    vertexShader: `varying vec2 vUv; void main(){ vUv=uv; gl_Position=projectionMatrix*modelViewMatrix*vec4(position,1.0); }`,
    fragmentShader: `
      varying vec2 vUv;
      uniform float time, intensity;
      uniform vec3 color1, color2;
      void main(){
        vec2 uv = vUv;
        float band = smoothstep(0.95, 0.25, abs(uv.y*2.0-1.0));
        float sweep = 0.5 + 0.5 * sin(time*0.12 + uv.x*5.0 + sin(uv.y*3.0+time*0.05));
        float a = intensity * band * sweep * 0.6;
        vec3 col = mix(color1, color2, 0.5 + 0.5*sin(time*0.05 + uv.x*2.0));
        gl_FragColor = vec4(col, a);
      }`,
  });

  const auroraPlane = new THREE.Mesh(new THREE.PlaneGeometry(2, 2), auroraMat);
  const fitAuroraToCamera = () => {
    const dist = 1.8;
    const h = 2 * Math.tan(THREE.MathUtils.degToRad(camera.fov * 0.5)) * dist;
    const w = h * (camera.aspect || 1);
    auroraPlane.scale.set(w, h, 1);
    auroraPlane.position.set(0, 0, -dist);
  };
  fitAuroraToCamera();
  camera.add(auroraPlane);
  auroraPlane.visible = false;

  const SKY_R = 5800;
  const skyLayer = new THREE.Group();
  skyLayer.name = 'SkyLayer';
  skyLayer.renderOrder = -940;
  scene.add(skyLayer);

  function createStarField(count = 160000, radius = 5000) {
    const geo = new THREE.BufferGeometry();
    const pos = new Float32Array(count * 3);
    const phase = new Float32Array(count);
    const sizeA = new Float32Array(count);
    const brightA = new Float32Array(count);
    for (let i = 0; i < count; i++) {
      const u = Math.random(), v = Math.random();
      const theta = 2 * Math.PI * u;
      const phi = Math.acos(2 * v - 1);
      const r = radius;
      pos[i * 3 + 0] = r * Math.sin(phi) * Math.cos(theta);
      pos[i * 3 + 1] = r * Math.cos(phi);
      pos[i * 3 + 2] = r * Math.sin(phi) * Math.sin(theta);
      phase[i] = Math.random() * Math.PI * 2;
      sizeA[i] = 0.8 + Math.random() * 1.0;
      brightA[i] = 0.5 + Math.random() * 0.9;
    }
    geo.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    geo.setAttribute('phase', new THREE.BufferAttribute(phase, 1));
    geo.setAttribute('sizeA', new THREE.BufferAttribute(sizeA, 1));
    geo.setAttribute('brightA', new THREE.BufferAttribute(brightA, 1));
    const mat = new THREE.ShaderMaterial({
      transparent: true,
      depthWrite: false,
      depthTest: false,
      uniforms: {
        time: { value: 0 },
        baseAlpha: { value: 1.0 },
        size: { value: 2.2 },
        pixelRatio: { value: Math.min(2, window.devicePixelRatio || 1) },
      },
      vertexShader: `
        uniform float size;
        attribute float phase,sizeA,brightA;
        varying float vPhase,vBright;
        void main(){
          vPhase=phase; vBright=brightA;
          vec4 mv=modelViewMatrix*vec4(position,1.0);
          gl_PointSize=size*sizeA*(320.0/max(1.0,-mv.z));
          gl_Position=projectionMatrix*mv;
        }`,
      fragmentShader: `
        uniform float time, baseAlpha;
        varying float vPhase,vBright;
        void main(){
          float tw = baseAlpha + 0.18 * sin(time*0.9 + vPhase);
          vec2 d = gl_PointCoord - 0.5;
          float m = smoothstep(0.30, 0.0, dot(d,d));
          gl_FragColor = vec4(vec3(1.0), vBright * tw * m);
        }`,
    });
    const pts = new THREE.Points(geo, mat);
    pts.renderOrder = -900;
    pts.frustumCulled = false;
    return { pts, mat };
  }

  const { pts: starField, mat: starMat } = createStarField(1600, 5000);
  starMat.uniforms.baseAlpha.value = 0.95;
  starMat.uniforms.size.value = 2.0;
  skyLayer.add(starField);
  starField.visible = false;

  const galaxyTex = textureLoader.load('/textures/729684.jpg', (t) => {
    t.colorSpace = THREE.SRGBColorSpace;
    t.minFilter = THREE.LinearMipMapLinearFilter;
    t.magFilter = THREE.LinearFilter;
    t.wrapS = t.wrapT = THREE.ClampToEdgeWrapping;
    t.center.set(0.5, 0.5);
    const maxAniso = (renderer.capabilities as any).getMaxAnisotropy?.() ?? 1;
    t.anisotropy = maxAniso;
  });

  const galaxyMat = new THREE.MeshBasicMaterial({
    map: galaxyTex,
    side: THREE.BackSide,
    transparent: true,
    opacity: 0.35,
    depthWrite: false,
    depthTest: false,
  });
  const galaxySphere = new THREE.Mesh(new THREE.SphereGeometry(5900, 64, 32), galaxyMat);
  galaxySphere.renderOrder = -945;
  skyLayer.add(galaxySphere);
  galaxySphere.visible = false;

  // Meteore
  function makeTrailTexture() {
    const scale = 2;
    const c = document.createElement('canvas');
    c.width = 128 * scale;
    c.height = 8 * scale;
    const g = c.getContext('2d')!;
    const grd = g.createLinearGradient(0, 0, c.width, 0);
    grd.addColorStop(0.0, 'rgba(255,255,255,0)');
    grd.addColorStop(0.15, 'rgba(255,255,255,0.25)');
    grd.addColorStop(0.50, 'rgba(255,255,255,1)');
    grd.addColorStop(1.00, 'rgba(255,255,255,0)');
    g.fillStyle = grd; g.fillRect(0, 0, c.width, c.height);
    const tex = new THREE.CanvasTexture(c);
    tex.minFilter = THREE.LinearMipMapLinearFilter;
    tex.magFilter = THREE.LinearFilter;
    return tex;
  }
  const meteorTex = makeTrailTexture();
  const meteorBaseMat = new THREE.MeshBasicMaterial({
    map: meteorTex,
    transparent: true,
    blending: THREE.AdditiveBlending,
    depthWrite: false,
    depthTest: true,
    side: THREE.DoubleSide,
  });
  type Meteor = {
    mesh: THREE.Mesh, axis: THREE.Vector3, angle: number, omega: number, life: number, maxLife: number, light?: THREE.PointLight|null
  };
  const meteors: Meteor[] = [];
  function randomUnitVec() {
    const u = Math.random(), v = Math.random();
    const theta = 2 * Math.PI * u;
    const phi = Math.acos(2 * v - 1);
    return new THREE.Vector3(
      Math.sin(phi) * Math.cos(theta),
      Math.cos(phi),
      Math.sin(phi) * Math.sin(theta)
    );
  }



const METEOR_PALETTE = [
  new THREE.Color('#5cc0ff'), // blau
  new THREE.Color('#ff5e5e'), // rot
  new THREE.Color('#65ff8a'), // grün
];




  const METEOR_BASE_LEN = 420, METEOR_BASE_WID = 16;
  function spawnMeteor() {
    const n = randomUnitVec();
    const start = n.clone().multiplyScalar(SKY_R);

    let t = new THREE.Vector3().crossVectors(n, new THREE.Vector3(0,1,0));
    if (t.lengthSq() < 1e-3) t = new THREE.Vector3().crossVectors(n, new THREE.Vector3(1,0,0));
    t.normalize();

    const axis  = new THREE.Vector3().crossVectors(n, t).normalize();
    const omega = THREE.MathUtils.degToRad(55 + Math.random()*25);

    const lenScale     = THREE.MathUtils.randFloat(0.6, 2.2);
    const widScale     = THREE.MathUtils.randFloat(0.9, 1.8);
    const lifeSpan     = THREE.MathUtils.randFloat(0.8, 2.4);
    const startOpacity = THREE.MathUtils.randFloat(0.7, 1.0);

    const geo = new THREE.PlaneGeometry(METEOR_BASE_LEN, METEOR_BASE_WID);
    const mat = meteorBaseMat.clone() as THREE.MeshBasicMaterial;
    mat.opacity = startOpacity;

    // ~75% farbige Meteore (sonst weiß)
    let glowLight: THREE.PointLight | null = null;
    if (Math.random() < 0.75) {
      const tint = METEOR_PALETTE[(Math.random() * METEOR_PALETTE.length) | 0];
      mat.color = tint.clone();

      // kleines Add-Glow am Kopf
      glowLight = new THREE.PointLight(tint, 1.2, 220, 2.0);
      glowLight.position.copy(start);
      skyLayer.add(glowLight);
    }

    const mesh = new THREE.Mesh(geo, mat);
    mesh.position.copy(start);
    mesh.quaternion.setFromUnitVectors(new THREE.Vector3(1,0,0), t);
    mesh.renderOrder = -935;
    mesh.scale.set(lenScale, widScale, 1);
    skyLayer.add(mesh);

    meteors.push({ mesh, axis, angle: 0, omega, life: 0, maxLife: lifeSpan, light: glowLight });
  }


  let currentPreset: SkyPreset = 'day';
  function setSkyPreset(preset: SkyPreset) {
    currentPreset = preset;
    auroraPlane.visible = false;
    starField.visible = false;
    galaxySphere.visible = false;
    skyDome.visible = true;

    if (preset === 'day') {
      skyUniforms.topColor.value.set('#7AB8FF');
      skyUniforms.bottomColor.value.set('#EAF7FF');
      skyUniforms.exponent.value = 1.4;
    }
    if (preset === 'aurora') {
      skyUniforms.topColor.value.set('#0b1225');
      skyUniforms.bottomColor.value.set('#12203a');
      skyUniforms.exponent.value = 0.9;
      auroraUniforms.intensity.value = 0.55;
      auroraPlane.visible = true;
    }
    if (preset === 'night') {
      skyUniforms.topColor.value.set('#02060f');
      skyUniforms.bottomColor.value.set('#090e1d');
      skyUniforms.exponent.value = 0.8;
      starField.visible = true;
    }
    if (preset === 'galaxy') {
      skyDome.visible = false;
      galaxySphere.visible = true;
      starField.visible = true;
      skyUniforms.topColor.value.set('#06080f');
      skyUniforms.bottomColor.value.set('#0b1020');
      skyUniforms.exponent.value = 0.9;
    }
  }
  (controls as any).setSkyPreset = setSkyPreset;
  (controls as any).cycleSkyPreset = () => {
    const order: SkyPreset[] = ['day', 'aurora', 'night', 'galaxy'];
    const i = order.indexOf(currentPreset);
    setSkyPreset(order[(i + 1) % order.length]);
  };
  setSkyPreset('day');

  // ============== Kamera-Intro ==============
  const INTRO_MS = 5000;
  const START_POS = new THREE.Vector3(0, 200, 500);
  const END_POS = new THREE.Vector3(-160, 100, 50);
  const START_LOOK = new THREE.Vector3(0, 0, 0);
  const END_LOOK = new THREE.Vector3(20, 0, 0);
  const START_FOV = camera.fov;
  const END_FOV = 58;
  const easeInOut = (t: number) => (t < 0.5 ? 2 * t * t : 1 - ((-2 * t + 2) ** 2) / 2);
  let introActive = false, clockStart = 0, userInteracted = false;
  const tmpLook = new THREE.Vector3();
  const prevEnabled = controls.enabled;
  const markInteracted = () => { userInteracted = true; };
  renderer.domElement.addEventListener('pointerdown', markInteracted);
  renderer.domElement.addEventListener('wheel', markInteracted, { passive: true });

  // Optionaler Kamera-Pfad fürs Intro (z.B. Dorf: durchs Tor bis zum Marktplatz).
  // Wird von SolarSimulation aus den echten Objektpositionen des geladenen
  // Dorf-Modells berechnet. Ohne gesetzten Pfad fliegt das alte Fix-Lerp-Intro.
  let introPathCurve: THREE.CatmullRomCurve3 | null = null;
  let introPathEndLook: THREE.Vector3 | null = null;
  let introDurationMs = INTRO_MS;
  (controls as any).setIntroFlightPath = (
    pts: Array<[number, number, number]> | null,
    endLook?: [number, number, number],
    durationMs = 11000
  ) => {
    if (!pts || pts.length < 2 || !endLook) {
      introPathCurve = null; introPathEndLook = null; introDurationMs = INTRO_MS;
      return;
    }
    introPathCurve = new THREE.CatmullRomCurve3(pts.map((p) => new THREE.Vector3(...p)), false, 'catmullrom', 0.35);
    introPathEndLook = new THREE.Vector3(...endLook);
    introDurationMs = durationMs;
  };

  function startIntro() {
    clockStart = performance.now();
    introActive = true;
    userInteracted = false;
    controls.enabled = false;
  }
  function finishIntro(cancelled: boolean) {
    introActive = false;
    if (cancelled) { if (tmpLook.lengthSq() === 0) tmpLook.copy(introPathEndLook ?? END_LOOK); }
    else if (introPathCurve && introPathEndLook) {
      // Pfad-Intro (Dorf): am Kurvenende stehen bleiben, Blick aufs Ziel (Marktplatz)
      introPathCurve.getPointAt(1, camera.position);
      tmpLook.copy(introPathEndLook);
    } else {
      tmpLook.copy(END_LOOK);
      camera.position.copy(END_POS);
      camera.fov = END_FOV; camera.updateProjectionMatrix();
    }
    const prevDamping = controls.enableDamping;
    controls.enabled = false; controls.enableDamping = false;
    // Pfad-Intro: camera.position wurde oben bereits auf das Kurvenende gesetzt
    const pos = (cancelled || introPathCurve) ? camera.position : END_POS;
    const look = tmpLook;
    const anyControls = controls as any;
    if (typeof anyControls.setLookAt === 'function') {
      anyControls.setLookAt(pos.x, pos.y, pos.z, look.x, look.y, look.z, false);
    } else {
      controls.target.copy(look); controls.object.position.copy(pos); controls.object.lookAt(look);
    }
    try { anyControls.sphericalDelta?.set(0,0,0); anyControls.zoomDelta = 0; anyControls.panOffset?.set(0,0,0); } catch {}
    controls.update(); controls.enableDamping = prevDamping; controls.enabled = prevEnabled;
    controls.enabled = true;
    /* Genau EINMAL melden. Hier stand der Aufruf zweimal: Handler, die einen
     * Einmal-Zustand verbrauchen (etwa das Replay-Flag des Dorf-Flugs, das
     * entscheidet ob eine pausierte Simulation pausiert bleibt), sahen beim
     * zweiten Aufruf bereits den zurueckgesetzten Wert und fielen auf ihren
     * Default zurueck — ein pausierter Lauf startete dadurch von selbst wieder. */
    (controls as any).onIntroFinished?.();
  }
  (controls as any).startIntro = startIntro;

  // ============== Ambient Flythrough (Loop, vor Login) ==============
  // Kurzer, enger Loop direkt um den Hauscluster (statt eines langen Ausflugs
  // über leeres Terrain zu See/Yacht) — das Interessante soll die meiste Zeit im Bild sein.
  const AMBIENT_LOOP_MS = 17000;
  const ambientWaypoints = [
    new THREE.Vector3(20, 150, 340),
    new THREE.Vector3(-90, 105, 160),
    new THREE.Vector3(-240, 80, 20),
    new THREE.Vector3(-230, 95, -140),
    new THREE.Vector3(-40, 130, -190),
    new THREE.Vector3(140, 160, -40),
    new THREE.Vector3(160, 190, 220),
  ];
  let ambientCurve = new THREE.CatmullRomCurve3(ambientWaypoints, true, 'catmullrom', 0.4);
  let ambientLoopActive = false, ambientClockStart = 0, ambientAutoArmed = true;
  let ambientFocusBuilder = false; // Flythrough umkreist das Builder-Dorf statt der Siedlung
  let ambientLastT = 0;
  const ambientPos = new THREE.Vector3();
  const ambientAhead = new THREE.Vector3();
  const ambientLook = new THREE.Vector3();

  /** Orbit-Pfad um die Bounding-Box des Builder-Dorfs. Wird bei jedem Loop-
   * Durchlauf neu berechnet, weil die Haus-GLBs asynchron nachladen und die
   * Bbox anfangs nur einen Teil der Szene umfasst. */
  function computeBuilderAmbientCurve(): THREE.CatmullRomCurve3 | null {
    const bbox = new THREE.Box3().setFromObject(builderGroup);
    if (bbox.isEmpty() || !Number.isFinite(bbox.min.x)) return null;
    const center = bbox.getCenter(new THREE.Vector3());
    const size = bbox.getSize(new THREE.Vector3());
    const r = Math.max(120, Math.max(size.x, size.z) * 0.62 + 40);
    const pts: THREE.Vector3[] = [];
    const N = 7;
    for (let i = 0; i < N; i++) {
      const a = (i / N) * Math.PI * 2;
      const wobble = 0.85 + 0.25 * Math.sin(i * 2.3); // leicht unrund, wirkt weniger mechanisch
      pts.push(new THREE.Vector3(
        center.x + Math.cos(a) * r * wobble,
        Math.max(50, bbox.min.y + r * (0.38 + 0.16 * Math.abs(Math.sin(i * 1.7)))),
        center.z + Math.sin(a) * r * wobble
      ));
    }
    return new THREE.CatmullRomCurve3(pts, true, 'catmullrom', 0.4);
  }

  function focusAmbientOnBuilder() {
    ambientFocusBuilder = true;
    const c = computeBuilderAmbientCurve();
    if (c) ambientCurve = c;
  }

  // Expliziter Preview-Pfad (z.B. niedriger Straßen-Rundflug durchs Dorf statt
  // hohem Bbox-Orbit). Setzt eine eigene Umlaufdauer und deaktiviert das
  // automatische Neu-Berechnen aus der Bounding-Box.
  let ambientCustomPath = false;
  let ambientDurationMs = AMBIENT_LOOP_MS;
  let ambientCustomBasePts: Array<[number, number, number]> | null = null;

  /** Kurve aus den Basis-Punkten, Höhe an die reale Dorf-Höhe geklemmt: mindestens
   * (höchstes Gebäude + 25). So fliegt die Preview nie IN Gebäude hinein, bleibt aber
   * so tief wie möglich. Obergrenze 150 verhindert, dass ein einzelner hoch
   * schwebender Tracker den ganzen Rundflug in die Stratosphäre schiebt. */
  function buildCustomAmbientCurve(): THREE.CatmullRomCurve3 | null {
    if (!ambientCustomBasePts) return null;
    const bbox = new THREE.Box3().setFromObject(builderGroup);
    const topY = (!bbox.isEmpty() && Number.isFinite(bbox.max.y)) ? bbox.max.y : 0;
    const minY = Math.min(Math.max(topY + 25, 70), 150);
    return new THREE.CatmullRomCurve3(
      ambientCustomBasePts.map((p) => new THREE.Vector3(p[0], Math.max(p[1], minY), p[2])),
      true, 'catmullrom', 0.4
    );
  }

  (controls as any).setAmbientPath = (pts: Array<[number, number, number]>, durationMs = 40000) => {
    if (!pts || pts.length < 3) return;
    ambientCustomBasePts = pts;
    ambientCustomPath = true;
    ambientDurationMs = durationMs;
    const c = buildCustomAmbientCurve();
    if (c) ambientCurve = c;
  };

  function startAmbientLoop() {
    if (introActive) return;
    ambientClockStart = performance.now();
    ambientLoopActive = true;
    controls.enabled = false;
  }
  function stopAmbientLoop() {
    ambientAutoArmed = false;
    ambientLoopActive = false;
    controls.enabled = prevEnabled;
  }
  (controls as any).startAmbientLoop = startAmbientLoop;
  (controls as any).stopAmbientLoop = stopAmbientLoop;
  (controls as any).focusAmbientOnBuilder = focusAmbientOnBuilder;

  // ============== Picking / Panels common ==============
  const raycaster = new THREE.Raycaster();
  const ndc = new THREE.Vector2();
  const panelRoots: THREE.Object3D[] = [];

  const hideAllPanelInfos = () => {
    panelInfoMap.forEach((pi) => pi.setVisible(false));
    (controls as any).onPanelClicked?.(null); // Highlight in der Bestenliste löschen
  };
  let downX = 0, downY = 0, downTime = 0;
  const MOVE_THRESH = 5, TIME_THRESH = 350;
  let builderModeActive = false;
  let builderPlacementActive = false;

  function toNDC(clientX: number, clientY: number) {
    const rect = renderer.domElement.getBoundingClientRect();
    ndc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    ndc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
  }
  function pickPanelRoot(clientX: number, clientY: number) {
    toNDC(clientX, clientY);
    raycaster.setFromCamera(ndc, camera);
    const hits = raycaster.intersectObjects(panelRoots, true);
    if (!hits.length) return null;
    let obj: THREE.Object3D | null = hits[0].object;
    while (obj && !(obj as any).userData.__isPanelRoot && obj.parent) obj = obj.parent as THREE.Object3D;
    return obj && (obj as any).userData.__isPanelRoot ? obj : null;
  }
  // Klick auf einen Tracker schaltet NUR dessen eigene Info-Karte um — andere bereits
  // offene Karten bleiben stehen, mehrere können gleichzeitig offen sein. Nur ein Klick
  // ins Leere (kein Tracker getroffen) oder ESC schließt alle auf einmal (hideAllPanelInfos).
  function togglePanelInfo(root: THREE.Object3D) {
    const info = panelInfoMap.get(root.name);
    if (!info) return;
    // Schwebende 3D-Karte auf ALLEN Geräten (auch Touch — ausdrücklicher Wunsch).
    info.setVisible(!info.isVisible());
    // React-Seite informieren: Bestenliste highlightet die Zeile dieses Trackers
    (controls as any).onPanelClicked?.(info.isVisible() ? root.name : null);
  }
  function onPointerDown(ev: PointerEvent) { downX = ev.clientX; downY = ev.clientY; downTime = performance.now(); }
  function onPointerUp(ev: PointerEvent) {
    const dt = performance.now() - downTime;
    const dx = ev.clientX - downX, dy = ev.clientY - downY;
    if (dt > TIME_THRESH || Math.hypot(dx, dy) > MOVE_THRESH) return;
    if (builderModeActive) {
      if (!builderPlacementActive) {
        selectBuilderObjectAt(ev.clientX, ev.clientY);
        const root = pickPanelRoot(ev.clientX, ev.clientY);
        if (!root) { hideAllPanelInfos(); return; }
        togglePanelInfo(root);
      }
      return;
    }
    const root = pickPanelRoot(ev.clientX, ev.clientY);
    if (!root) { hideAllPanelInfos(); return; }
    togglePanelInfo(root);
  }
  function onEsc(e: KeyboardEvent) { if (e.key === 'Escape') hideAllPanelInfos(); }
  // Rechtsklick wählt im Self-Build ein bereits platziertes Objekt aus — OrbitControls
  // nutzt die rechte Maustaste standardmäßig fürs Schwenken (Pan), daher hier per
  // downX/downY/downTime (von onPointerDown oben) dieselbe Klick-vs-Drag-Unterscheidung
  // wie beim Linksklick: nur ein echter Klick (kein Pan-Drag) wählt aus.
  function onContextMenu(e: MouseEvent) {
    if (!builderModeActive) return;
    const dt = performance.now() - downTime;
    const dx = e.clientX - downX, dy = e.clientY - downY;
    e.preventDefault();
    if (dt > TIME_THRESH || Math.hypot(dx, dy) > MOVE_THRESH) return; // war ein Pan-Drag, keine Auswahl
    if (builderPlacementActive) builderCancelPlacing();
    selectBuilderObjectAt(e.clientX, e.clientY);
  }
  renderer.domElement.addEventListener('pointerdown', onPointerDown);
  renderer.domElement.addEventListener('pointerup', onPointerUp);
  renderer.domElement.addEventListener('contextmenu', onContextMenu);
  window.addEventListener('keydown', onEsc);

  // ============== Lights / Sun ==============
  scene.add(new THREE.AmbientLight(0xffffff, 0.6));
  const hemi = new THREE.HemisphereLight(0xBBDDEE, 0x223344, 0.7);
  scene.add(hemi);
  const amb = new THREE.AmbientLight(0xffffff, 0.15);
  scene.add(amb);

  const sun = new Sun(textureLoader);
  sun.addToScene(scene);
  sunLightRef.current = sun.light;
  sunSphereRef.current = sun.mesh;
  sunLightRef.current.castShadow = true;
  sunLightRef.current.shadow.mapSize.width = 1024;
  sunLightRef.current.shadow.mapSize.height = 1024;

  // Mond: reine Anzeige-Kugel, wird vom bestehenden sun.light automatisch phasenkorrekt
  // beleuchtet (siehe Moon.ts) — Position wird von außen (SolarSimulation.tsx) über
  // moonSphereRef gesetzt, exakt wie bei sunSphereRef.
  const moon = new Moon();
  moon.addToScene(scene);
  if (moonSphereRef) moonSphereRef.current = moon.mesh;


  // ============== Terrain ==============
  textureLoader.load('/textures/custom_heightmap.png', (texture) => {
    if (disposed) {
      disposeTexture(texture);
      return;
    }
    // Weltgröße 5x hochgezogen (Playground-Platz für Self-Build), Segmentzahl bewusst
    // unverändert gelassen: die Höhenkarten-Zuordnung unten indiziert Vertices 1:1 gegen
    // Heightmap-Pixel (i % canvas.width) und bricht, wenn die Vertexanzahl nicht mehr zur
    // Bildauflösung passt — nur width/height (Weltmaß) skalieren, nie 256/256 anfassen.
    const geometry = new THREE.PlaneGeometry(2500, 2500, 256, 256);
    const vertices = geometry.attributes.position as THREE.BufferAttribute;
    const canvas = document.createElement('canvas');
    canvas.width = (texture.image as HTMLImageElement).width;
    canvas.height = (texture.image as HTMLImageElement).height;
    const ctx = canvas.getContext('2d');
    ctx?.drawImage(texture.image as CanvasImageSource, 0, 0);
    const imgData = ctx?.getImageData(0, 0, canvas.width, canvas.height).data;
    if (imgData) {
      for (let i = 0; i < vertices.count; i++) {
        const x = i % canvas.width;
        const y = Math.floor(i / canvas.width);
        const index = (y * canvas.width + x) * 4;
        const height = (imgData[index] / 255.0) * 3;
        (vertices as any).setZ(i, height);
      }
      geometry.computeVertexNormals();
      const greenTex = textureLoader.load('/textures/grass_texture.png');
      greenTex.wrapS = greenTex.wrapT = THREE.RepeatWrapping;
      greenTex.repeat.set(50, 50); // proportional zur 5x größeren Weltfläche, sonst wirkt das Gras verwaschen
      const material = new THREE.MeshStandardMaterial({
        map: greenTex,
        displacementMap: texture,
        displacementScale: 3,
      });
      const terrain = new THREE.Mesh(geometry, material);
      terrain.rotation.x = -Math.PI / 2;
      terrain.position.set(0, -5, 0);
      terrain.receiveShadow = true;
      scene.add(terrain);
    }
  }, undefined, (err) => warnAssetLoad('/textures/custom_heightmap.png', err));

  // kleines Deko-Licht
  const glow = new THREE.PointLight(0xff00ff, 0.8, 20);
  glow.position.set(0, 12, 0);
  scene.add(glow);

  // ============== HELPERS für Panel-Sets ==============
  const clearPanels = () => {
    for (let i = panelRoots.length - 1; i >= 0; i--) {
      const p = panelRoots[i];
      // Never remove builder-placed trackers — they live in builderGroup independently
      if (p.name.startsWith('b_tracker_')) continue;
      p.parent?.remove(p);
      disposeObjectTree(p);
      panelRoots.splice(i, 1);
    }
    trackerMap.forEach((_v, key) => {
      if (!key.startsWith('b_tracker_')) trackerMap.delete(key);
    });
    panelInfoMap.forEach((info, key) => {
      if (!key.startsWith('b_tracker_')) { info.dispose(); panelInfoMap.delete(key); }
    });
  };

  function addPanelsFromConfigs(factory: SolarTracker, configs: Array<any>, attachTo: THREE.Group) {
    configs.forEach((config) => {
      const panel = factory.create(config);
      (panel as any).energyDay = 0;
      (panel as any).energyHistory = [];
      panel.userData.__isPanelRoot = true;
      panel.receiveShadow = true;  // Panel empfängt Schatten
      panel.name = config.name;
      attachTo.add(panel);
      trackerMap.set(panel.name, panel as any);
      panelRoots.push(panel);
      const panelInfo = new PanelInfo(scene, panel as any);
      panelInfo.setVisible(false);
      panelInfoMap.set(panel.name, panelInfo);
    });
  }

  // ============== LOADERS: Häuser/Umgebung ==============


  // nur 1x definieren (außerhalb der Loader), damit du es wiederverwenden kannst
  //
  // War früher MeshBasicMaterial (komplett unlit) — viele Material-Slots dieses
  // Modells sind reine Flächenfarben ohne Textur (z.B. "Farbe weiß"), und unlit
  // heißt: keinerlei Schattierung, also eine flache weiße/graue Fläche ohne jede
  // Tiefe. MeshLambertMaterial reagiert auf die vorhandenen Ambient-/Hemisphere-/
  // Sonnenlichter (siehe oben), bleibt aber günstig und bekommt keine PBR-Reflexe.
  const toUnlit = (mat: THREE.Material) => {
    const m: any = mat;
    const basic = new THREE.MeshLambertMaterial({
      map: m.map ?? null,
      color: m.color ? m.color.clone() : new THREE.Color(0xffffff),
      transparent: m.transparent ?? false,
      opacity: m.opacity ?? 1,
      side: m.side ?? THREE.FrontSide,
      vertexColors: m.vertexColors ?? false,
      alphaMap: m.alphaMap ?? null,
      depthWrite: m.depthWrite ?? true,
      depthTest: m.depthTest ?? true,
    });
    basic.name = (m.name ?? '') + '_unlit';
    return basic;
  };

  const warnAssetLoad = (path: string, err: unknown) => {
    console.warn(`Optional asset could not be loaded: ${path}`, err);
  };

  const createGLTFLoader = () => {
    const loader = new GLTFLoader();
    loader.setMeshoptDecoder(MeshoptDecoder);
    return loader;
  };

  const loadGLTF = (loader: GLTFLoader, path: string, onLoad: (gltf: any) => void, onError?: () => void) => {
    loader.load(path, (gltf) => {
      if (disposed) {
        disposeObjectTree(gltf.scene);
        return;
      }
      // Mobile: auch Siedlungs-/Einzelhaus-Assets (25SOL etc.) verkleinern —
      // die laufen nicht über loadBuilderModel, laden aber immer mit.
      // Best effort: ein Fehler hier darf das Asset nicht verhindern.
      try { shrinkTexturesForMobile(gltf.scene); } catch (err) { console.warn('Textur-Verkleinerung übersprungen:', path, err); }
      onLoad(gltf);
    }, undefined, (err) => { warnAssetLoad(path, err); onError?.(); });
  };

  // Customer projects do not need the demonstration houses, lakes or yacht.
  if (!isProject) {
  // Kleines Haus in singleGroup
  const gltfSingle = createGLTFLoader();
  loadGLTF(gltfSingle, '/models/25SOL Umgebung 02 30 Grad.glb', (gltf) => {


  singleHouse = gltf.scene;

  // 👉 genau hier: auf selbstleuchtend (unlit) umbauen
  singleHouse.traverse((obj: any) => {
    if (obj.isMesh) {
      if (Array.isArray(obj.material)) {
        obj.material = obj.material.map(toUnlit);
      } else {
        obj.material = toUnlit(obj.material);
      }
      obj.castShadow = false;
      obj.receiveShadow = false;
    }
  });

  singleHouse.visible = true;
  singleHouse.scale.set(5, 5, 5);
  singleHouse.position.set(0, 0, 0);
  singleGroup.add(singleHouse);
  reportAssetLoaded(true);
  }, () => reportAssetLoaded(true));

const gltfLoader6 = createGLTFLoader();
loadGLTF(gltfLoader6, '/models/25SOL Umgebung 02.glb', (gltf) => {
  const duplex_house4 = gltf.scene;
  duplex_house4.scale.set(5, 5, 5);
  duplex_house4.position.set(-220, 0, 0);

  const addConstantGlow = (mat: THREE.Material) => {
    const m = mat as any;
    if (!(m.isMeshStandardMaterial || m.isMeshPhysicalMaterial)) return mat;

    // Set emissive to a very subtle intensity to simulate constant light
    m.emissive.set(0xeeeeee);  // Subtle light gray to simulate constant "shine"
    m.emissiveIntensity = 0.05; // Very subtle constant light

    // Retain original colors and set roughness to maintain non-reflectivity
    m.metalness = 0;    // No metal effect
    m.roughness = 1;    // Very rough, no gloss

    // Make sure the shading stays even, no shadows
    m.flatShading = true;
    m.needsUpdate = true;

    return m;
  };

  duplex_house4.traverse((obj: any) => {
    if (obj.isMesh && obj.material) {
      if (Array.isArray(obj.material)) {
        obj.material = obj.material.map(addConstantGlow);
      } else {
        obj.material = addConstantGlow(obj.material);
      }
      // Disabling shadows completely to avoid sunlight effects
      obj.castShadow = false;
      obj.receiveShadow = false;
    }
  });

  // Adding the houses to the neighborhood group
  neighborhoodGroup.add(duplex_house4);
  reportAssetLoaded(true);
}, () => reportAssetLoaded(true));




  const gltfLoader7 = createGLTFLoader();
  loadGLTF(gltfLoader7, '/models/frozen_lake.glb', (gltf) => {
    const lake = gltf.scene;
    lake.rotation.y = 2 * (-Math.PI / 2) + Math.PI;
    lake.scale.set(500, 500, 500);
    lake.position.set(700, -83, -250);
    lake.receiveShadow = true;
    neighborhoodGroup.add(lake);
    reportAssetLoaded();
  }, reportAssetLoaded);

  const gltfLake = createGLTFLoader();
  loadGLTF(gltfLake, '/models/frozen_lake.glb', (gltf) => {
    const lake = gltf.scene;
    lake.rotation.y = -Math.PI;
    lake.scale.set(-500, 500, 500);
    lake.position.set(700, -83, 245);
    lake.receiveShadow = true;
    neighborhoodGroup.add(lake);
    reportAssetLoaded();
  }, reportAssetLoaded);

  const gltfLoader8 = createGLTFLoader();
  loadGLTF(gltfLoader8, '/models/frickies_yacht.glb', (gltf) => {
    const yacht = gltf.scene;
    yacht.rotation.y = Math.PI / 3;
    yacht.scale.set(70, 70, 70);
    yacht.position.set(630, -2, -20);
    yacht.receiveShadow = true;
    neighborhoodGroup.add(yacht);
    reportAssetLoaded();
  }, reportAssetLoaded);

  }

  // ============== Panels laden & Mode-Toggle ==============
  const gltfPanels = createGLTFLoader();
  loadGLTF(gltfPanels, '/models/Solartracker TEST NEU.glb', (gltf) => {
    const trackerModel = gltf.scene;
    trackerModel.scale.set(5, 5, 5);

    const factory = new SolarTracker(scene, new THREE.MeshStandardMaterial(), trackerModel);

    // A) Multi-Set (deine bestehenden)
    const multiConfigs = [

    //0
      { name: 'fixed_roof1', type: 'fixed', position: [-120, 17, -32], mountingDeg: 0, compass: 'süd' },
      { name: 'fixed_roof2', type: 'fixed', position: [-80, 17, -28], mountingDeg: 0, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'fixed_roof3', type: 'fixed', position: [120, 17, -32], mountingDeg: 0, compass: 'nord' },
      { name: 'fixed_roof4', type: 'fixed', position: [160, 17, -28], mountingDeg: 0, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },


       { name: 'single_south_0', type: 'single', position: [-120,5, -110], mountingDeg: 0, compass: 'süd' },
            { name: 'single_west_0', type: 'single', position: [0, 5, -110], mountingDeg: 0, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
            { name: 'single_nord_0', type: 'single', position: [120, 5, -110], mountingDeg: -0, compass: 'nord' },
            { name: 'single_east_0', type: 'single', position: [250, 5, -110], mountingDeg: 0, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },



    //30°
      { name: 'dual_south', type: 'limited', position: [-138, 38, -23], mountingDeg: 30, compass: 'süd' },
      { name: 'dual_west', type: 'limited', position: [-205, 41, -8], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'dual_nord', type: 'limited', position: [-150, 38, -42], mountingDeg: -30, compass: 'nord', alignment: 180 },
      { name: 'dual_east', type: 'limited', position: [-185, 41, -65], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

      { name: 'fixed_south', type: 'fixed', position: [-150, 32, -23], mountingDeg: 30, compass: 'süd' },
      { name: 'fixed_west', type: 'fixed', position: [-205, 36, -33], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'fixed_nord', type: 'fixed', position: [-138, 32, -42], mountingDeg: -30, compass: 'nord' },
      { name: 'fixed_east', type: 'fixed', position: [-185, 36, -45], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

      { name: 'dual_south-unlimited', type: 'unlimited', position: [-165, 41, -23], mountingDeg: 30, compass: 'süd' },
      { name: 'dual_west-unlimited', type: 'unlimited', position: [-205, 43, -50], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'dual_nord-unlimited', type: 'unlimited', position: [-165, 43, -42], mountingDeg: -30, compass: 'nord' },
      { name: 'dual_east-unlimited', type: 'unlimited', position: [-185, 43, -10], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

       { name: 'single_south_30', type: 'single', position: [-210,5, -110], mountingDeg: 30, compass: 'süd' },
       { name: 'single_west_30', type: 'single', position: [-190, 5, -110], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
       { name: 'single_nord_30', type: 'single', position: [-170, 5, -110], mountingDeg: -30, compass: 'nord' },
       { name: 'single_east_30', type: 'single', position: [-150, 5, -110], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },




      // 35°
      { name: 'dual_south_35', type: 'limited', position: [-60, 42, -23], mountingDeg: 35, compass: 'süd' },
      { name: 'dual_west_35', type: 'limited', position: [-20, 43, -8], mountingDeg: 35, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'dual_nord_35', type: 'limited', position: [-60, 42, -42], mountingDeg: -35, compass: 'nord', alignment: 180 },
      { name: 'dual_east_35', type: 'limited', position: [0, 43, -65], mountingDeg: 35, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

      { name: 'fixed_south_35', type: 'fixed', position: [-45, 32, -23], mountingDeg: 35, compass: 'süd' },
      { name: 'fixed_west_35', type: 'fixed', position: [-20, 38, -53], mountingDeg: 35, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'fixed_nord_35', type: 'fixed', position: [-45, 32, -42], mountingDeg: -35, compass: 'nord' },
      { name: 'fixed_east_35', type: 'fixed', position: [0, 38, -45], mountingDeg: 35, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

      { name: 'dual_south-unlimited_35', type: 'unlimited', position: [-30, 42, -23], mountingDeg: 35, compass: 'süd' },
      { name: 'dual_west-unlimited_35', type: 'unlimited', position: [-20, 43, -70], mountingDeg: 35, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'dual_nord-unlimited_35', type: 'unlimited', position: [-30, 42, -42], mountingDeg: -35, compass: 'nord' },
      { name: 'dual_east-unlimited_35', type: 'unlimited', position: [0, 43, -10], mountingDeg: 35, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },


       { name: 'single_south_35', type: 'single', position: [-90,5, -110], mountingDeg: 35, compass: 'süd' },
       { name: 'single_west_35', type: 'single', position: [-70, 5, -110], mountingDeg: 35, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
       { name: 'single_nord_35', type: 'single', position: [-50, 5, -110], mountingDeg: -35, compass: 'nord' },
       { name: 'single_east_35', type: 'single', position: [-25, 5, -110], mountingDeg: 35, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },


      // 40°
      { name: 'dual_south_40', type: 'limited', position: [102, 43, -23], mountingDeg: 40, compass: 'süd' },
      { name: 'dual_west_40', type: 'limited', position: [35, 45, -13], mountingDeg: 40, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'dual_nord_40', type: 'limited', position: [90, 43, -42], mountingDeg: -40, compass: 'nord', alignment: 180 },
      { name: 'dual_east_40', type: 'limited', position: [55, 43, -65], mountingDeg: 40, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

      { name: 'fixed_south_40', type: 'fixed', position: [90, 34, -23], mountingDeg: 40, compass: 'süd' },
      { name: 'fixed_west_40', type: 'fixed', position: [35, 38, -43], mountingDeg: 40, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'fixed_nord_40', type: 'fixed', position: [102, 34, -42], mountingDeg: -40, compass: 'nord' },
      { name: 'fixed_east_40', type: 'fixed', position: [55, 38, -45], mountingDeg: 40, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

      { name: 'dual_south-unlimited_40', type: 'unlimited', position: [75, 43, -23], mountingDeg: 40, compass: 'süd' },
      { name: 'dual_west-unlimited_40', type: 'unlimited', position: [35, 43, -70], mountingDeg: 40, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'dual_nord-unlimited_40', type: 'unlimited', position: [75, 45, -42], mountingDeg: -40, compass: 'nord' },
      { name: 'dual_east-unlimited_40', type: 'unlimited', position: [55, 45, -10], mountingDeg: 40, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

       { name: 'single_south_40', type: 'single', position: [30,5, -110], mountingDeg: 40, compass: 'süd' },
       { name: 'single_west_40', type: 'single', position: [50, 5, -110], mountingDeg: 40, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
       { name: 'single_nord_40', type: 'single', position: [70, 5, -110], mountingDeg: -40, compass: 'nord' },
       { name: 'single_east_40', type: 'single', position: [90, 5, -110], mountingDeg: 40, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },



      // 45°
      { name: 'dual_south_45', type: 'limited', position: [180, 41, -23], mountingDeg: 45, compass: 'süd' },
      { name: 'dual_west_45', type: 'limited', position: [220, 50, -13], mountingDeg: 45, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'dual_nord_45', type: 'limited', position: [180, 40, -42], mountingDeg: -45, compass: 'nord', alignment: 180 },
      { name: 'dual_east_45', type: 'limited', position: [240, 45, -65], mountingDeg: 45, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

      { name: 'fixed_south_45', type: 'fixed', position: [195, 40, -23], mountingDeg: 45, compass: 'süd' },
      { name: 'fixed_west_45', type: 'fixed', position: [220, 40, -68], mountingDeg: 45, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'fixed_nord_45', type: 'fixed', position: [195, 40, -42], mountingDeg: -45, compass: 'nord' },
      { name: 'fixed_east_45', type: 'fixed', position: [240, 40, -45], mountingDeg: 45, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

      { name: 'dual_south-unlimited_45', type: 'unlimited', position: [210, 47, -23], mountingDeg: 45, compass: 'süd' },
      { name: 'dual_west-unlimited_45', type: 'unlimited', position: [220, 50, -50], mountingDeg: 45, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
      { name: 'dual_nord-unlimited_45', type: 'unlimited', position: [210, 47, -42], mountingDeg: -45, compass: 'nord' },
      { name: 'dual_east-unlimited_45', type: 'unlimited', position: [240, 50, -10], mountingDeg: 45, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },

       { name: 'single_south_45', type: 'single', position: [170,5, -110], mountingDeg: 45, compass: 'süd' },
       { name: 'single_west_45', type: 'single', position: [190, 5, -110], mountingDeg: 45, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
       { name: 'single_nord_45', type: 'single', position: [210, 5, -110], mountingDeg: -45, compass: 'nord' },
       { name: 'single_east_45', type: 'single', position: [230, 5, -110], mountingDeg: 45, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },





    ];

    // B) Kompaktes Single-Set fürs kleine Haus (Beispiel-Positionen)
    const singleConfigs = [

  { name: 'dual_south', type: 'limited', position: [70, 38, -25], mountingDeg: 30, compass: 'süd' },
        { name: 'dual_west', type: 'limited', position: [15, 41, -20], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: true, mountFlip: false, compass: 'west' },
        { name: 'dual_nord', type: 'limited', position: [70, 38, -42], mountingDeg: -30, compass: 'nord', alignment: 180 },
        { name: 'dual_east', type: 'limited', position: [35, 41, -65], mountingDeg: 30, alignment: 90, mountingAxis: 'z', mountFirst: false, mountFlip: false, compass: 'ost' },


    ];

    // Panels initial: Multi — skip if builder mode already active (e.g. Digital Twin loaded first)
    if (!builderModeActive) {
      addPanelsFromConfigs(factory, multiConfigs, neighborhoodGroup);
    }

    function setHouseMode(next: HouseMode) {
      if (next === houseMode) return;
      houseMode = next;
      neighborhoodGroup.visible = next === 'multi' && !builderModeActive;
      singleGroup.visible = next === 'single' && !builderModeActive;
      clearPanels();
      // Never re-add regular panels while builder mode is active
      if (builderModeActive) return;
      if (next === 'multi') {
        addPanelsFromConfigs(factory, multiConfigs, neighborhoodGroup);
      } else {
        addPanelsFromConfigs(factory, singleConfigs, singleGroup);
      }
    }
    function toggleHouseMode() {
      setHouseMode(houseMode === 'multi' ? 'single' : 'multi');
    }
    (controls as any).setHouseMode = setHouseMode;
    (controls as any).toggleHouseMode = toggleHouseMode;

    trackerFactoryRef = factory;
  });

  // ============== BUILDER MODE ==============
  const builderGroup = new THREE.Group();
  builderGroup.name = 'BuilderGroup';
  builderGroup.visible = false;
  scene.add(builderGroup);

  // Invisible flat terrain mesh for raycasting — matches visible grass terrain size (2500x2500),
  // so Self-Build placement clicks register everywhere the visible ground actually extends.
  const builderTerrainGeo = new THREE.PlaneGeometry(2500, 2500);
  const builderTerrainMat = new THREE.MeshBasicMaterial({ visible: false, side: THREE.DoubleSide });
  const builderTerrainMesh = new THREE.Mesh(builderTerrainGeo, builderTerrainMat);
  builderTerrainMesh.rotation.x = -Math.PI / 2;
  builderTerrainMesh.position.set(0, -3, 0);
  builderTerrainMesh.visible = false;
  scene.add(builderTerrainMesh);

  const builderRaycaster = new THREE.Raycaster();
  const builderNdc = new THREE.Vector2();

  let placingConfig: any = null;
  let ghostMesh: THREE.Object3D | null = null;
  let placementRotationDeg = 0;
  let placementScaleMultiplier = 1;
  const builderPlacedObjects: THREE.Object3D[] = [];
  type BuilderObjectKind = 'house' | 'tracker' | 'obstacle';
  type BuilderObjectRecord = {
    id: string;
    kind: BuilderObjectKind;
    label: string;
    object: THREE.Object3D;
    cfg: any;
    rotDeg: number;
  };
  const builderPlacedRecords: BuilderObjectRecord[] = [];
  const loadedBuilderModels = new Map<string, THREE.Object3D>();
  let trackerFactoryRef: any = null;
  // Lade-Generation: wird bei jedem clearBuilderScene() erhöht; asynchrone
  // Platzierungs-Callbacks prüfen dagegen und verwerfen sich selbst, wenn sie
  // zu einem überholten Ladevorgang gehören (Duplikat-Schutz).
  let builderLoadEpoch = 0;

  // ===== GLB-Laden: dedupliziert + gedrosselt =====
  // Ohne das startete loadBuilderSnapshot für JEDE Platzierung einen eigenen
  // parallelen Download+Parse — bei einem Dorf mit 200+ Objekten also ~200
  // gleichzeitige Parses derselben ~60 Dateien. Folgen: massiver Speicher-Burst
  // beim Intro-Laden (jede Kopie hält eigene Geometrie/Texturen, Klone teilen
  // dann nichts mehr) und unter Last still fehlschlagende Textur-Decodes —
  // sichtbar als komplett weiße Modelle (z.B. Palmen). Jetzt: ein Load pro Datei
  // (In-Flight-Promise-Map), maximal MAX_PARALLEL_GLB_LOADS gleichzeitig.
  // Mobil nur 2 parallele Loads: der Spitzen-Speicherbedarf während des Ladens
  // (rohe GLB-Puffer + volle 1024er-Bitmaps vor dem Verkleinern) halbiert sich —
  // iOS Safari killt Tabs nach Spitzen, nicht nach Durchschnitt.
  const MAX_PARALLEL_GLB_LOADS = (window.matchMedia?.('(pointer: coarse)').matches ?? false) ? 2 : 4;
  const builderModelLoads = new Map<string, Promise<THREE.Object3D>>();
  let activeGlbLoads = 0;
  const glbLoadQueue: Array<() => void> = [];
  function enqueueGlbLoad<T>(fn: () => Promise<T>): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const run = () => {
        activeGlbLoads++;
        // Promise.resolve().then(fn): auch ein SYNCHRON werfendes fn gibt den
        // Queue-Slot garantiert wieder frei — ein geleakter Slot legt sonst
        // dauerhaft alle weiteren Ladevorgänge lahm.
        Promise.resolve().then(fn).then(resolve, reject).finally(() => {
          activeGlbLoads--;
          glbLoadQueue.shift()?.();
        });
      };
      if (activeGlbLoads < MAX_PARALLEL_GLB_LOADS) run();
      else glbLoadQueue.push(run);
    });
  }
  /** Touch-Geräte: Texturen nach dem Laden auf max. 512px verkleinern. Die
   * ~60 Dorf-Modelle mit je 4 Texturen à 1024² belegen sonst ~1,3 GB GPU-
   * Speicher (inkl. Mipmaps) — iOS Safari beendet den Tab dann hart. Bei 512²
   * sind es ~330 MB; auf einem 6-Zoll-Display ist der Unterschied unsichtbar.
   * Läuft einmal pro Modell (der Cache teilt Texturen über alle Klone). */
  function shrinkTexturesForMobile(root: THREE.Object3D, maxSize = 512) {
    if (!(window.matchMedia?.('(pointer: coarse)').matches ?? false)) return;
    const seen = new Set<THREE.Texture>();
    const SLOTS = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'alphaMap'];
    root.traverse((n: any) => {
      if (!n.isMesh || !n.material) return;
      const mats = Array.isArray(n.material) ? n.material : [n.material];
      for (const m of mats) {
        if (!m) continue;
        for (const slot of SLOTS) {
          const tex: THREE.Texture | undefined = m[slot];
          const img: any = tex?.image;
          if (!tex || seen.has(tex) || !img?.width || Math.max(img.width, img.height) <= maxSize) continue;
          seen.add(tex);
          const scale = maxSize / Math.max(img.width, img.height);
          const c = document.createElement('canvas');
          c.width = Math.max(1, Math.round(img.width * scale));
          c.height = Math.max(1, Math.round(img.height * scale));
          const ctx = c.getContext('2d');
          if (!ctx) continue;
          ctx.drawImage(img, 0, 0, c.width, c.height);
          tex.image = c;
          tex.needsUpdate = true;
          if (typeof img.close === 'function') img.close(); // ImageBitmap-Speicher sofort freigeben
        }
      }
    });
  }

  /** Mobile: CPU-Kopien der Vertex-Daten nach dem GPU-Upload freigeben.
   * three.js behält sonst JEDES Attribut zusätzlich im JS-Heap (für Raycasts) —
   * bei ~55 Dorf-Modellen mehrere hundert MB, die iOS Safari das Genick brechen.
   * Trade-off auf Touch-Geräten: Rechtsklick-Objektauswahl per Raycast auf
   * Häuser funktioniert dann nicht mehr (Energie-Shading nutzt die Boxen nur
   * bei aktiviertem Schatten-Flag; Desktop bleibt komplett unverändert). */
  function releaseGeometryCpuCopiesForMobile(root: THREE.Object3D) {
    if (!(window.matchMedia?.('(pointer: coarse)').matches ?? false)) return;
    function disposeArray(this: any) { this.array = null; }
    const seenBuffers = new Set<any>();
    const release = (attr: any) => {
      // meshopt-optimierte GLBs nutzen INTERLEAVED Attribute — dort sitzt
      // onUpload auf dem geteilten InterleavedBuffer (attr.data), nicht auf dem
      // Attribut selbst. Der frühere direkte onUpload-Aufruf warf deshalb einen
      // TypeError, der Lade-Promise nie auflöste und die ganze Queue stallte
      // ("es laden nur noch die Tracker").
      const target = attr?.isInterleavedBufferAttribute ? attr.data : attr;
      if (!target || typeof target.onUpload !== 'function' || seenBuffers.has(target)) return;
      seenBuffers.add(target);
      target.onUpload(disposeArray);
    };
    root.traverse((n: any) => {
      if (!n.isMesh || !n.geometry) return;
      const g = n.geometry as THREE.BufferGeometry;
      // Bounding-Daten VOR dem Freigeben berechnen — Auto-Scale/Ghosts brauchen sie
      if (!g.boundingBox) g.computeBoundingBox();
      if (!g.boundingSphere) g.computeBoundingSphere();
      for (const key of Object.keys(g.attributes)) release((g.attributes as any)[key]);
      release(g.index);
    });
  }

  function loadBuilderModel(path: string): Promise<THREE.Object3D> {
    const cached = loadedBuilderModels.get(path);
    if (cached) return Promise.resolve(cached);
    const inFlight = builderModelLoads.get(path);
    if (inFlight) return inFlight;
    const p = enqueueGlbLoad(() => new Promise<THREE.Object3D>((resolve, reject) => {
      const loader = createGLTFLoader();
      loader.load(path, (gltf) => {
        // Best effort: schlägt eine Mobile-Optimierung fehl, lieber unoptimiert
        // rendern als gar nicht. Und resolve() MUSS immer erreichbar bleiben —
        // ein hängendes Promise blockiert sonst dauerhaft einen Queue-Slot.
        try {
          shrinkTexturesForMobile(gltf.scene);
          // Project houses remain raycastable for selection and energy shading.
          if (!new URLSearchParams(window.location.search).has('project')) releaseGeometryCpuCopiesForMobile(gltf.scene);
        } catch (err) {
          console.warn('Mobile-Optimierung übersprungen für', path, err);
        }
        loadedBuilderModels.set(path, gltf.scene);
        resolve(gltf.scene);
      }, undefined, reject);
    }));
    builderModelLoads.set(path, p);
    // Fehlgeschlagene Loads aus der Map nehmen, damit ein erneuter Versuch möglich bleibt
    p.catch(() => builderModelLoads.delete(path));
    return p;
  }

  /** Site-Modelle (vom solar-tracker-Backend generierte Kundenhaus-GLBs) werden
   * nicht aus /models/ geladen, sondern über einen von SolarSimulation gesetzten
   * Provider, der die Bytes same-origin über /api/site-model holt und eine
   * blob:-URL liefert (CSP: connect-src erlaubt nur 'self' und blob:). Im
   * Snapshot-cfg steht dafür NUR {source:'site-model', siteId} — niemals die
   * blob:-URL selbst, die überlebt keinen Reload. */
  let metricSettings=projectMetrics();
  let siteModelUrlProvider: ((siteId: string) => Promise<string>) | null = null;
  function setSiteModelUrlProvider(fn: (siteId: string) => Promise<string>) {
    siteModelUrlProvider = fn;
  }
  function resolveHouseModelUrl(cfg: any): Promise<string> {
    if (cfg?.source === 'site-model' && cfg?.siteId) {
      if (!siteModelUrlProvider) {
        return Promise.reject(new Error('Site-Modell-Provider nicht verfügbar (kein Digital-Twin-Modus)'));
      }
      return Promise.resolve(siteModelUrlProvider(cfg.siteId));
    }
    return Promise.resolve(`/models/${cfg.modelFile}`);
  }

  // Für Modelle ohne von-Hand kalibrierte cfg.scale: einmalig anhand der echten
  // Bounding-Box auf eine Zielgröße normalisieren, statt eine Zahl zu raten.
  const autoScaleCache = new Map<string, number>();
  function getAutoScale(path: string, rawScene: THREE.Object3D, targetMaxDim = 70): number {
    const cached = autoScaleCache.get(path);
    if (cached != null) return cached;
    const box = new THREE.Box3().setFromObject(rawScene);
    const size = box.getSize(new THREE.Vector3());
    const maxDim = Math.max(size.x, size.y, size.z);
    const scale = Number.isFinite(maxDim) && maxDim > 0 ? targetMaxDim / maxDim : 1;
    autoScaleCache.set(path, scale);
    return scale;
  }

  // Live occluder list for builder-placed objects (houses, obstacles, trackers)
  const builderOccluderMeshes: THREE.Mesh[] = [];
  function collectMeshes(obj: THREE.Object3D): THREE.Mesh[] {
    const result: THREE.Mesh[] = [];
    obj.traverse((child: any) => { if (child.isMesh) result.push(child as THREE.Mesh); });
    return result;
  }
  function addBuilderOccluders(obj: THREE.Object3D) {
    const meshes = collectMeshes(obj);
    obj.userData.__occluderMeshes = meshes;
    builderOccluderMeshes.push(...meshes);
  }
  function removeBuilderOccluders(obj: THREE.Object3D) {
    const meshes: THREE.Mesh[] = obj.userData.__occluderMeshes ?? [];
    for (const m of meshes) {
      const idx = builderOccluderMeshes.indexOf(m);
      if (idx >= 0) builderOccluderMeshes.splice(idx, 1);
    }
    obj.userData.__occluderMeshes = [];
  }
  let builderObjectSeq = 0;
  let selectedBuilderObjectId: string | null = null;
  let builderSelectionHelper: THREE.BoxHelper | null = null;

  let builderMoveHandler: ((e: PointerEvent) => void) | null = null;
  let builderClickHandler: ((e: PointerEvent) => void) | null = null;

  function disposeBuilderSelectionHelper() {
    if (!builderSelectionHelper) return;
    scene.remove(builderSelectionHelper);
    builderSelectionHelper.geometry.dispose();
    (builderSelectionHelper.material as THREE.Material).dispose();
    builderSelectionHelper = null;
  }

  function getBuilderObjectSummaries() {
    return builderPlacedRecords.map(({ id, kind, label }) => ({ id, kind, label }));
  }

  function getBuilderSnapshot() {
    return builderPlacedRecords.map(({ kind, label, cfg, rotDeg, object }) => {
      // Tracker: IMMER die Basisposition speichern, nie die Live-Position.
      // Sonst entsteht eine Absink-Ratsche: Wer nachts (geparkt, y = base−drop)
      // speichert, lädt den Tracker beim nächsten Mal 5 Einheiten tiefer als
      // Basis — und jedes weitere Speichern/Laden senkt ihn erneut ab.
      const base = kind === 'tracker' ? (object as any).basePosition : null;
      const p = base ?? object.position;
      return {
        kind,
        label,
        cfg,
        rotDeg,
        position: { x: p.x, y: p.y, z: p.z },
      };
    });
  }

  function loadBuilderSnapshot(snapshot: ReturnType<typeof getBuilderSnapshot>) {
    const pending: Promise<unknown>[] = [];
    // If trackerFactory not ready yet, retry after models have loaded
    const hasTrackers = snapshot.some(i => i.kind === 'tracker');
    if (hasTrackers && !trackerFactoryRef) {
      const retryMs = 200;
      const maxRetries = 25; // 5 seconds total
      let attempts = 0;
      const wait = setInterval(() => {
        attempts++;
        if (trackerFactoryRef || attempts >= maxRetries) {
          clearInterval(wait);
          if (trackerFactoryRef) loadBuilderSnapshot(snapshot);
        }
      }, retryMs);
      // Still place houses/obstacles immediately
    }
    clearBuilderScene();
    // Diese Lade-Generation merken: kommt ein GLB-Load erst NACH einem weiteren
    // clearBuilderScene() zurück (Nutzer lädt erneut / Tracker-Retry), gehört
    // seine Platzierung zu einer alten Generation und wird verworfen — vorher
    // stapelten sich dadurch Duplikate unter den Originalen.
    const epoch = builderLoadEpoch;
    for (const item of snapshot) {
      const { kind, label, cfg, rotDeg, position } = item;
      if (kind === 'house') {
        const place = (path: string, gltfScene: THREE.Object3D) => {
          if (disposed || epoch !== builderLoadEpoch) return;
          const obj = gltfScene.clone();
          const s = cfg.scale ?? getAutoScale(path, gltfScene, cfg.fitSize ?? 70);
          restoreBuildingScale(obj,cfg,s);
          obj.position.set(position.x, position.y, position.z);
          obj.rotation.y = THREE.MathUtils.degToRad(rotDeg);
          let effCfg = {...cfg,scale:s};
          if (cfg.groundSnap) {
            // Nur für synthetisch eingefügte Objekte (?site=-Kundenhaus), deren
            // Snapshot-Position kein echter Platzierungsstand ist: einmalig wie
            // finalizePlacement zentrieren + Unterkante auf den Builder-Boden
            // setzen. Danach das Flag aus dem cfg entfernen — beim Speichern
            // steht dann die echte Objektposition im Snapshot, und ein erneuter
            // Snap würde sie sonst bei jedem Laden weiter verschieben.
            obj.updateWorldMatrix(true, true);
            const bbox = new THREE.Box3().setFromObject(obj);
            if (Number.isFinite(bbox.min.x) && Number.isFinite(bbox.max.x)) {
              const center = bbox.getCenter(new THREE.Vector3());
              obj.position.x += position.x - center.x;
              obj.position.z += position.z - center.z;
              obj.updateWorldMatrix(true, true);
              bbox.setFromObject(obj);
            }
            if (Number.isFinite(bbox.min.y)) {
              obj.position.y += builderTerrainMesh.position.y - bbox.min.y;
            }
            const { groundSnap: _dropped, ...rest } = effCfg;
            effCfg = rest;
          }
          if (cfg.preserveMaterials) {
            preserveBuilderMaterials(obj);
          } else {
            applyUnlitToObject(obj);
          }
          builderGroup.add(obj);
          addBuilderOccluders(obj);
          registerBuilderObject(obj, 'house', label, effCfg, rotDeg);
        };
        pending.push(resolveHouseModelUrl(cfg)
          .then((path) => loadBuilderModel(path).then((gltfScene) => place(path, gltfScene)))
          .catch((err) => { console.warn('Snapshot house load failed:', err); if(isProject)throw err;return false; }));
      } else if (kind === 'obstacle') {
        const obj = buildObstacle(cfg);
        obj.scale.setScalar(cfg.scale ?? 1);
        obj.position.set(position.x, position.y, position.z);
        obj.rotation.y = THREE.MathUtils.degToRad(rotDeg);
        builderGroup.add(obj);
        addBuilderOccluders(obj);
        registerBuilderObject(obj, 'obstacle', label, cfg, rotDeg);
      } else if (kind === 'tracker' && trackerFactoryRef) {
        const name = `b_tracker_${Date.now()}_${Math.random().toString(36).slice(2)}`;
        // Wie finalizePlacement: vereinheitlichte Doppelwinkel-Ausrichtung aus
        // Kompass + gespeicherter Rotation (siehe Kommentar dort).
        const COMPASS_AZ: Record<string, number> = { süd: 0, ost: 90, nord: 180, west: 270 };
        const baseAz = COMPASS_AZ[cfg.compass ?? 'süd'] ?? 0;
        const az = ((baseAz + rotDeg) % 360 + 360) % 360;
        const trackerCfg = {
          name,
          type: cfg.type ?? cfg.trackerType ?? 'limited',
          position: [position.x, position.y, position.z] as [number, number, number],
          mountingDeg: cfg.mountingDeg ?? 30,
          yawDeg: az, // wie finalizePlacement: expliziter Rack-Azimut
          compass: cfg.compass ?? 'süd',
          mountingAxis: 'x' as const,
          mountFirst: false,
          mountFlip: false,
          panelCount: cfg.panelCount ?? 4,
          panelColor: cfg.panelColor,
          panelEfficiency: cfg.panelEfficiency ?? 1,
          frameColor: cfg.frameColor ?? null,
          panelOrientation: cfg.panelOrientation ?? 'portrait',
          metrics: cfg.metrics,
          parkDrop: cfg.metrics ? cfg.metrics.park_drop_m*PROJECT_UNITS_PER_METRE : undefined,
          activeLift: cfg.metrics ? cfg.metrics.active_lift_m*PROJECT_UNITS_PER_METRE : 6, // wie finalizePlacement: aktive Tracking-Höhe
          sizeScale: cfg.trackerScale ?? 2, // wie finalizePlacement: Builder-Maßstab
        };
        const panel = (trackerFactoryRef as any).create(trackerCfg);
        panel.userData.__isPanelRoot = true;
        panel.name = name;
        panel.position.set(position.x, position.y, position.z);
        // KEIN rotation.y mehr: create() setzt die volle Orientierung aus dem
        // Kompass; eine zusätzliche Y-Rotation würde bis zum ersten Sonnen-Tick
        // eine falsche Ausrichtung zeigen.
        builderGroup.add(panel);
        addBuilderOccluders(panel);
        trackerMap.set(name, panel as any);
        panelRoots.push(panel);
        const pi = new PanelInfo(scene, panel as any);
        pi.setVisible(false);
        panelInfoMap.set(name, pi);
        registerBuilderObject(panel, 'tracker', label, cfg, rotDeg);
      }
    }
    builderGroup.visible = true;
    return Promise.all(pending);
  }

  function notifyBuilderObjectsChanged() {
    (controls as any).onBuilderObjectsChanged?.(getBuilderObjectSummaries());
  }

  function notifyBuilderSelectionChanged() {
    (controls as any).onBuilderSelectionChanged?.(selectedBuilderObjectId);
  }

  function selectBuilderObject(id: string | null) {
    selectedBuilderObjectId = id;
    disposeBuilderSelectionHelper();
    if (id) {
      const record = builderPlacedRecords.find((item) => item.id === id);
      if (record) {
        builderSelectionHelper = new THREE.BoxHelper(record.object, 0x38bdf8);
        scene.add(builderSelectionHelper);
      }
    }
    notifyBuilderSelectionChanged();
  }

  function getSelectedBuilderTransform() {
    const record = builderPlacedRecords.find((item) => item.id === selectedBuilderObjectId);
    if (!record) return null;
    // Tracker: aktuelle Dachhöhe (Basis über Builder-Boden) fürs Auswahl-Panel,
    // damit sie dort präzise nachjustiert werden kann.
    const base = (record.object as any).basePosition;
    const roofHeight = record.kind === 'tracker' && base
      ? Math.round((base.y - builderTerrainMesh.position.y) * 100) / 100
      : undefined;
    const mountingDeg = record.kind === 'tracker'
      ? Math.round(Math.abs((record.object as any).mountingDeg ?? 30))
      : undefined;
    return { rotDeg: record.rotDeg, scale: record.object.scale.x, label: record.label, kind: record.kind, roofHeight, mountingDeg };
  }

  /** Dachhöhe eines ausgewählten Trackers live ändern: verschiebt die Basis-
   * position; die sichtbare Position setzt der (idempotente) Park-/Lift-Tick. */
  function builderSetSelectedHeight(roofH: number) {
    const record = builderPlacedRecords.find((item) => item.id === selectedBuilderObjectId);
    if (!record || record.kind !== 'tracker') return;
    const base = (record.object as any).basePosition;
    if (!base) return;
    base.y = builderTerrainMesh.position.y + roofH;
    record.cfg = { ...record.cfg, roofHeight: roofH }; // konsistent für Duplizieren
    updateSunPosition?.();
    notifyBuilderSelectionChanged();
  }

  /** Neigung eines ausgewählten Trackers live ändern — der nächste Orientierungs-
   * Tick baut die Basispose (Yaw ⨯ Kippung) mit dem neuen Winkel neu auf. */
  function builderSetSelectedMounting(deg: number) {
    const record = builderPlacedRecords.find((item) => item.id === selectedBuilderObjectId);
    if (!record || record.kind !== 'tracker') return;
    (record.object as any).mountingDeg = deg;
    record.cfg = { ...record.cfg, mountingDeg: deg }; // konsistent für Duplizieren/Snapshot
    updateSunPosition?.();
  }

  function builderSetSelectedRotation(deg: number) {
    const record = builderPlacedRecords.find((item) => item.id === selectedBuilderObjectId);
    if (!record) return;
    if (record.kind === 'tracker') {
      // Tracker: rotation.y würde der SunTracker beim nächsten Tick überschreiben.
      // Stattdessen die Tracking-Ausrichtung selbst umrechnen (Doppelwinkel-Formel,
      // identisch zur Platzierung) und sofort einen Orientierungs-Tick auslösen,
      // damit der Slider auch bei pausierter Simulation live wirkt.
      const COMPASS_AZ: Record<string, number> = { süd: 0, ost: 90, nord: 180, west: 270 };
      const baseAz = COMPASS_AZ[record.cfg?.compass ?? 'süd'] ?? 0;
      const az = ((baseAz + deg) % 360 + 360) % 360;
      (record.object as any).yawDeg = az;
      record.rotDeg = deg;
      updateSunPosition?.();
      return;
    }
    record.object.rotation.y = THREE.MathUtils.degToRad(deg);
    record.rotDeg = deg;
  }

  function builderSetSelectedScale(scale: number) {
    const record = builderPlacedRecords.find((item) => item.id === selectedBuilderObjectId);
    if (!record) return;
    record.object.scale.multiplyScalar(scale/record.object.scale.x);
    record.cfg = { ...record.cfg, scale, ...(record.cfg.scale_xyz?{scale_xyz:record.object.scale.toArray()}:{}), metricReference: undefined };
    notifyBuilderSelectionChanged();
  }

  /** Dupliziert das ausgewählte Objekt: startet eine neue Platzierung mit exakt demselben
   * cfg (inkl. schon fest gebackener Skalierung) und derselben Rotation — folgt danach dem
   * Cursor wie eine frische Platzierung, ein Klick setzt die Kopie ab. */
  function builderDuplicateSelected() {
    const record = builderPlacedRecords.find((item) => item.id === selectedBuilderObjectId);
    if (!record) return;
    placementRotationDeg = record.rotDeg;
    placementScaleMultiplier = 1; // record.cfg.scale ist schon die fertige Endgröße
    builderStartPlacing({ ...record.cfg, kind: record.kind, label: record.label });
  }

  let builderMoveSelectedHandler: ((e: PointerEvent) => void) | null = null;
  let builderMoveSelectedUpHandler: ((e: PointerEvent) => void) | null = null;

  function removeBuilderMoveSelectedListeners() {
    if (builderMoveSelectedHandler) renderer.domElement.removeEventListener('pointermove', builderMoveSelectedHandler);
    if (builderMoveSelectedUpHandler) renderer.domElement.removeEventListener('pointerup', builderMoveSelectedUpHandler);
    builderMoveSelectedHandler = null;
    builderMoveSelectedUpHandler = null;
  }

  /** Bewegen per Klick-Vorschau: nach Aufruf folgt das ausgewählte Objekt dem Cursor über
   * den Boden, bis geklickt (fixieren) oder ESC (abbrechen) gedrückt wird — dasselbe
   * klickbasierte Muster wie beim Erstplatzieren, kein Drag nötig, keine Kollision mit
   * OrbitControls' Rotate/Pan-Drag. */
  function builderStartMoveSelected() {
    const record = builderPlacedRecords.find((item) => item.id === selectedBuilderObjectId);
    if (!record) return;
    builderCancelPlacing();
    removeBuilderMoveSelectedListeners();

    builderMoveSelectedHandler = (e: PointerEvent) => {
      const pt = getBuilderGroundPoint(e.clientX, e.clientY);
      if (!pt) return;
      record.object.position.x = pt.x;
      record.object.position.z = pt.z;
      if (record.kind === 'tracker') {
        // Tracker schweben auf Dachhöhe — NICHT auf den Boden snappen. Die
        // Park-Basis zieht mit, sonst teleportiert das Nacht-Parken
        // (parkLimited setzt die Position aus basePosition) den Tracker zurück.
        const base = (record.object as any).basePosition;
        if (base) { base.x = pt.x; base.z = pt.z; }
      } else {
        record.object.updateWorldMatrix(true, true);
        const bbox = new THREE.Box3().setFromObject(record.object);
        if (Number.isFinite(bbox.min.y)) {
          record.object.position.y += pt.y - bbox.min.y;
        }
      }
      disposeBuilderSelectionHelper();
      builderSelectionHelper = new THREE.BoxHelper(record.object, 0x38bdf8);
      scene.add(builderSelectionHelper);
    };
    builderMoveSelectedUpHandler = (e: PointerEvent) => {
      if (e.button !== 0) return;
      removeBuilderMoveSelectedListeners();
      (controls as any).onBuilderMoveFinished?.();
    };
    renderer.domElement.addEventListener('pointermove', builderMoveSelectedHandler);
    renderer.domElement.addEventListener('pointerup', builderMoveSelectedUpHandler);
  }

  function registerBuilderObject(object: THREE.Object3D, kind: BuilderObjectKind, label: string, cfg: any = {}, rotDeg = 0) {
    const id = `builder_${++builderObjectSeq}`;
    object.userData.__builderObjectId = id;
    object.userData.__builderKind = kind;
    object.userData.__builderLabel = label;
    object.traverse((child: any) => {
      child.userData.__builderObjectId = id;
      child.userData.__builderKind = kind;
    });

    builderPlacedObjects.push(object);
    builderPlacedRecords.push({ id, kind, label, object, cfg, rotDeg });
    notifyBuilderObjectsChanged();
    selectBuilderObject(id);
    return id;
  }

  function disposeBuilderObject(record: BuilderObjectRecord) {
    builderGroup.remove(record.object);

    if (trackerMap.has(record.object.name)) {
      trackerMap.delete(record.object.name);
      panelInfoMap.get(record.object.name)?.dispose();
      panelInfoMap.delete(record.object.name);
      const idx = panelRoots.indexOf(record.object);
      if (idx >= 0) panelRoots.splice(idx, 1);
    }

    removeBuilderOccluders(record.object);

    if (record.kind === 'obstacle') {
      disposeObjectTree(record.object);
    }
  }

  function deleteBuilderObject(id: string) {
    const recordIndex = builderPlacedRecords.findIndex((item) => item.id === id);
    if (recordIndex < 0) return false;

    const [record] = builderPlacedRecords.splice(recordIndex, 1);
    const objectIndex = builderPlacedObjects.indexOf(record.object);
    if (objectIndex >= 0) builderPlacedObjects.splice(objectIndex, 1);

    if (selectedBuilderObjectId === id) selectBuilderObject(null);
    disposeBuilderObject(record);
    notifyBuilderObjectsChanged();
    return true;
  }

  function pickBuilderObject(clientX: number, clientY: number) {
    if (!builderPlacedRecords.length) return null;
    toNDC(clientX, clientY);
    raycaster.setFromCamera(ndc, camera);
    // try/catch: auf Touch-Geräten sind die CPU-Kopien der Geometrie freigegeben
    // (releaseGeometryCpuCopiesForMobile) — Raycasts gegen solche Meshes werfen.
    // Auswahl ist dort ein Nice-to-have, kein Grund für einen App-Crash.
    let hits: THREE.Intersection[] = [];
    try {
      hits = raycaster.intersectObjects(builderPlacedRecords.map((item) => item.object), true);
    } catch { return null; }
    if (!hits.length) return null;

    const objectId = (hits[0].object as any).userData.__builderObjectId;
    return objectId ? builderPlacedRecords.find((item) => item.id === objectId) ?? null : null;
  }

  function selectBuilderObjectAt(clientX: number, clientY: number) {
    const record = pickBuilderObject(clientX, clientY);
    selectBuilderObject(record?.id ?? null);
  }

  function getBuilderGroundPoint(clientX: number, clientY: number): THREE.Vector3 | null {
    const rect = renderer.domElement.getBoundingClientRect();
    builderNdc.x = ((clientX - rect.left) / rect.width) * 2 - 1;
    builderNdc.y = -((clientY - rect.top) / rect.height) * 2 + 1;
    builderRaycaster.setFromCamera(builderNdc, camera);
    const hits = builderRaycaster.intersectObject(builderTerrainMesh);
    return hits.length > 0 ? hits[0].point : null;
  }

  function makeBoxGhost(w: number, h: number, d: number, color: number): THREE.Group {
    const geo = new THREE.BoxGeometry(w, h, d);
    const fill = new THREE.Mesh(geo, new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.22 }));
    const edges = new THREE.EdgesGeometry(geo);
    const wire = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color, transparent: true, opacity: 0.85 }));
    const g = new THREE.Group();
    g.add(fill, wire);
    g.position.y = h / 2;
    return g;
  }

  function makeObstacleGhost(cfg: any): THREE.Object3D {
    if (cfg.obstacleType === 'footprint' && Array.isArray(cfg.rings_m)) {
      const rings=cfg.rings_m.map((ring:number[][])=>ring.map(([x,z])=>new THREE.Vector2(x*PROJECT_UNITS_PER_METRE,-z*PROJECT_UNITS_PER_METRE)));
      const outline=new THREE.Shape(rings[0]);outline.holes=rings.slice(1).map((points:THREE.Vector2[])=>new THREE.Path(points));
      const geometry=new THREE.ExtrudeGeometry(outline,{depth:cfg.height,bevelEnabled:false});geometry.rotateX(-Math.PI/2);
      const mesh=new THREE.Mesh(geometry,new THREE.MeshStandardMaterial({color:0x8c939a,roughness:.9}));mesh.castShadow=true;mesh.receiveShadow=true;return mesh;
    }
    if (cfg.obstacleType === 'tree') {
      const th = cfg.treeHeight ?? 20;
      const tr = cfg.treeCrownRadius ?? 6;
      const trunkH = th * 0.45;
      const crownH = th * 0.65;
      const g = new THREE.Group();
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(tr * 0.13, tr * 0.18, trunkH, 8), new THREE.MeshBasicMaterial({ color: 0x8B4513, transparent: true, opacity: 0.55 }));
      trunk.position.y = trunkH / 2;
      const crown = new THREE.Mesh(new THREE.ConeGeometry(tr, crownH, 8), new THREE.MeshBasicMaterial({ color: 0x228B22, transparent: true, opacity: 0.55 }));
      crown.position.y = trunkH + crownH / 2;
      g.add(trunk, crown);
      return g;
    } else if (cfg.obstacleType === 'wall') {
      return makeBoxGhost(cfg.width ?? 20, cfg.height ?? 15, cfg.depth ?? 3, 0x8a8a80);
    } else {
      return makeBoxGhost(cfg.width ?? 15, cfg.height ?? 25, cfg.depth ?? 15, 0xaaaaaa);
    }
  }

  function buildObstacle(cfg: any): THREE.Object3D {
    if (cfg.obstacleType === 'footprint' && Array.isArray(cfg.rings_m)) {
      const rings=cfg.rings_m.map((ring:number[][])=>ring.map(([x,z])=>new THREE.Vector2(x*PROJECT_UNITS_PER_METRE,-z*PROJECT_UNITS_PER_METRE)));
      const outline=new THREE.Shape(rings[0]);outline.holes=rings.slice(1).map((points:THREE.Vector2[])=>new THREE.Path(points));
      const geometry=new THREE.ExtrudeGeometry(outline,{depth:cfg.height,bevelEnabled:false});geometry.rotateX(-Math.PI/2);
      const mesh=new THREE.Mesh(geometry,new THREE.MeshStandardMaterial({color:0x8c939a,roughness:.9}));mesh.castShadow=true;mesh.receiveShadow=true;return mesh;
    }
    if (cfg.obstacleType === 'tree') {
      const th = cfg.treeHeight ?? 20;
      const tr = cfg.treeCrownRadius ?? 6;
      const trunkH = th * 0.45;
      const crownH = th * 0.65;
      const g = new THREE.Group();
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(tr * 0.13, tr * 0.18, trunkH, 8), new THREE.MeshStandardMaterial({ color: 0x8B4513 }));
      trunk.position.y = trunkH / 2; trunk.castShadow = true;
      const crown = new THREE.Mesh(new THREE.ConeGeometry(tr, crownH, 8), new THREE.MeshStandardMaterial({ color: 0x228B22 }));
      crown.position.y = trunkH + crownH / 2; crown.castShadow = true;
      g.add(trunk, crown);
      return g;
    } else if (cfg.obstacleType === 'wall') {
      const m = new THREE.Mesh(
        new THREE.BoxGeometry(cfg.width ?? 20, cfg.height ?? 15, cfg.depth ?? 3),
        new THREE.MeshStandardMaterial({ color: 0x8a8a80, roughness: 0.9 })
      );
      m.position.y = (cfg.height ?? 15) / 2;
      m.castShadow = true; m.receiveShadow = true;
      return m;
    } else {
      const m = new THREE.Mesh(
        new THREE.BoxGeometry(cfg.width ?? 15, cfg.height ?? 25, cfg.depth ?? 15),
        new THREE.MeshStandardMaterial({ color: 0xb0b0b0, roughness: 0.8 })
      );
      m.position.y = (cfg.height ?? 25) / 2;
      m.castShadow = true; m.receiveShadow = true;
      return m;
    }
  }

  function updateGhostRotation() {
    if (!ghostMesh) return;
    // Tracker-Ghost dreht sich mit Kompass-Grundrichtung PLUS Rotations-Slider —
    // exakt der Azimut, den auch die Platzierung verwendet.
    if (placingConfig?.kind === 'tracker') {
      const COMPASS_AZ: Record<string, number> = { süd: 0, ost: 90, nord: 180, west: 270 };
      const baseAz = COMPASS_AZ[placingConfig?.compass ?? 'süd'] ?? 0;
      ghostMesh.rotation.y = THREE.MathUtils.degToRad(baseAz + placementRotationDeg);
      return;
    }
    ghostMesh.rotation.y = THREE.MathUtils.degToRad(placementRotationDeg);
  }

  function updateGhostScale() {
    if (ghostMesh) ghostMesh.scale.setScalar(placementScaleMultiplier);
  }

  function removeBuilderListeners() {
    if (builderMoveHandler) { renderer.domElement.removeEventListener('pointermove', builderMoveHandler); builderMoveHandler = null; }
    if (builderClickHandler) { renderer.domElement.removeEventListener('pointerup', builderClickHandler); builderClickHandler = null; }
  }

  function applyUnlitToObject(obj: THREE.Object3D) {
    // MeshLambertMaterial statt MeshBasicMaterial: reagiert auf Ambient-/Sonnenlicht,
    // damit flächige Farb-Materialien (kein Textur-Map) eine echte Schattierung statt
    // eines flachen weißen/grauen Klotzes bekommen. Siehe toUnlit() weiter oben.
    obj.traverse((child: any) => {
      if (!child.isMesh) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      child.material = mats.map((m: any) => new THREE.MeshLambertMaterial({
        map: m.map ?? null,
        color: m.color ? m.color.clone() : new THREE.Color(0xffffff),
        transparent: m.transparent ?? false,
        opacity: m.opacity ?? 1,
        side: m.side ?? THREE.FrontSide,
        vertexColors: m.vertexColors ?? false,
      }));
      if (child.material.length === 1) child.material = child.material[0];
      child.castShadow = true;
      child.receiveShadow = true;
    });
  }

  function preserveBuilderMaterials(obj: THREE.Object3D) {
    const maxAnisotropy = renderer.capabilities.getMaxAnisotropy?.() ?? 1;
    const textureSlots = ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'emissiveMap', 'aoMap', 'alphaMap'];

    obj.traverse((child: any) => {
      if (!child.isMesh) return;
      const mats = Array.isArray(child.material) ? child.material : [child.material];
      child.material = mats.map((m: any) => {
        const mat = m?.clone ? m.clone() : m;
        for (const slot of textureSlots) {
          const tex = mat?.[slot];
          if (tex?.isTexture) {
            tex.anisotropy = Math.max(tex.anisotropy ?? 1, Math.min(8, maxAnisotropy));
            tex.needsUpdate = true;
          }
        }
        if (mat) mat.needsUpdate = true;
        return mat;
      });
      if (child.material.length === 1) child.material = child.material[0];
      child.castShadow = true;
      child.receiveShadow = true;
    });
  }

  function finalizePlacement(clientX: number, clientY: number) {
    const pt = getBuilderGroundPoint(clientX, clientY);
    if (!pt || !placingConfig) return;

    const cfg = placingConfig;
    const rot = placementRotationDeg;
    const scaleMult = placementScaleMultiplier;
    const groundY = pt.y; // y from terrain intersection

    if (cfg.kind === 'obstacle') {
      const obj = buildObstacle(cfg);
      // groundY from terrain hit; obstacles have internal Y-offset so base at groundY
      obj.position.set(pt.x, groundY, pt.z);
      obj.rotation.y = THREE.MathUtils.degToRad(rot);
      builderGroup.add(obj);
      addBuilderOccluders(obj);
      const id = registerBuilderObject(obj, 'obstacle', cfg.label ?? 'Hindernis', cfg, rot);
      (controls as any).onBuilderPlaced?.({ id, kind: 'obstacle', label: cfg.label ?? 'Hindernis', type: cfg.obstacleType });

    } else if (cfg.kind === 'house') {
      const pos = pt.clone();

      // Duplikat-Schutz: Wird die Szene geleert (Modell laden), während dieses
      // GLB noch lädt, verfällt die ausstehende Platzierung.
      const epoch = builderLoadEpoch;
      const placeIt = (path: string, gltfScene: THREE.Object3D) => {
        if (disposed || epoch !== builderLoadEpoch) return;
        const obj = gltfScene.clone();
        const s = (cfg.scale ?? getAutoScale(path, gltfScene, cfg.fitSize ?? 70)) * scaleMult;
        restoreBuildingScale(obj,cfg,s,scaleMult);
        obj.position.copy(pos);
        obj.rotation.y = THREE.MathUtils.degToRad(rot);
        obj.updateWorldMatrix(true, true);
        const bbox = new THREE.Box3().setFromObject(obj);
        if (cfg.centerOnPlacement && Number.isFinite(bbox.min.x) && Number.isFinite(bbox.max.x)) {
          const center = bbox.getCenter(new THREE.Vector3());
          obj.position.x += pos.x - center.x;
          obj.position.z += pos.z - center.z;
          obj.updateWorldMatrix(true, true);
          bbox.setFromObject(obj);
        }
        if (Number.isFinite(bbox.min.y)) {
          obj.position.y += groundY - bbox.min.y;
        }
        if (cfg.preserveMaterials) {
          preserveBuilderMaterials(obj);
        } else {
          applyUnlitToObject(obj);
        }
        builderGroup.add(obj);
        addBuilderOccluders(obj);
        // Resolved scale (preset/auto-fit x manueller Slider) fest ins gespeicherte cfg
        // backen — sonst würde ein Reload aus "Meine Modelle" die Slider-Anpassung verlieren
        // und wieder auf die Preset-/Auto-Fit-Größe zurückfallen.
        const id = registerBuilderObject(obj, 'house', cfg.label ?? 'Haus', { ...cfg, scale: s, ...(cfg.scale_xyz?{scale_xyz:obj.scale.toArray()}:{}), ...(scaleMult!==1?{metricReference:undefined}:{} ) }, rot);
        (controls as any).onBuilderPlaced?.({ id, kind: 'house', label: cfg.label ?? 'Haus', model: cfg.modelFile });
      };

      resolveHouseModelUrl(cfg)
        .then((path) => loadBuilderModel(path).then((gltfScene) => placeIt(path, gltfScene)))
        .catch((err) => console.warn('House model load failed:', err));

    } else if (cfg.kind === 'tracker' && trackerFactoryRef) {
      const name = `b_tracker_${Date.now()}`;
      const height = groundY + (cfg.roofHeight ?? 5);

      // Vereinheitlichte Ausrichtung: Die SunTracker-Basispose hat eine
      // Doppelwinkel-Beziehung (Panel-Azimut = 2·(alignment−180), numerisch
      // verifiziert; die vier alten, handkalibrierten Kompass-Configs sind exakt
      // Spezialfälle davon). Damit werden Kompass UND Rotations-Slider sauber
      // kombinierbar: Kompass liefert die Grundrichtung, der Slider dreht
      // stufenlos weiter — für Häuser, die nicht exakt N/S/O/W stehen.
      const COMPASS_AZ: Record<string, number> = { süd: 0, ost: 90, nord: 180, west: 270 };
      const baseAz = COMPASS_AZ[cfg.compass ?? 'süd'] ?? 0;
      const az = ((baseAz + rot) % 360 + 360) % 360;

      const trackerCfg = {
        name,
        type: cfg.trackerType ?? 'limited',
        position: [pt.x, height, pt.z] as [number, number, number],
        mountingDeg: cfg.mountingDeg ?? 30, // immer positiv — die Richtung steckt im Azimut
        yawDeg: az, // expliziter Rack-Azimut: gerades Gestell, Normal-Azimut = az
        compass: cfg.compass ?? 'süd',
        mountingAxis: 'x' as const,
        mountFirst: false,
        mountFlip: false,
        panelCount: cfg.panelCount ?? 4,
        panelColor: cfg.panelColor,
        panelEfficiency: cfg.panelEfficiency ?? 1,
        frameColor: cfg.frameColor ?? null,
        panelOrientation: cfg.panelOrientation ?? 'portrait',
        metrics: isProject ? (cfg.metrics ?? metricSettings) : undefined,
        parkDrop: isProject ? (cfg.metrics ?? metricSettings).park_drop_m*PROJECT_UNITS_PER_METRE : undefined,
        activeLift: isProject ? (cfg.metrics ?? metricSettings).active_lift_m*PROJECT_UNITS_PER_METRE : 6, // bei Sonne 6 Einheiten über die Basis hochfahren (aktive Tracking-Höhe)
        // Builder-Tracker doppelt so groß wie die Siedlungs-Tracker — das Rack-GLB
        // ist im Maßstab der 25SOL-Häuser gebaut und wirkt neben den Builder-Modellen
        // (fitSize-normiert) sonst wie Spielzeug.
        sizeScale: cfg.trackerScale ?? 2,
      };
      const panel = (trackerFactoryRef as any).create(trackerCfg);
      panel.userData.__isPanelRoot = true;
      panel.name = name;
      panel.receiveShadow = true;

      builderGroup.add(panel);
      addBuilderOccluders(panel);
      trackerMap.set(name, panel as any);
      panelRoots.push(panel);
      const pi = new PanelInfo(scene, panel as any);
      pi.setVisible(false);
      panelInfoMap.set(name, pi);
      // rot mitspeichern — geht beim Snapshot-Laden wieder in die Azimut-Formel ein
      const id = registerBuilderObject(panel, 'tracker', cfg.label ?? 'Tracker', isProject?{...cfg,metrics:cfg.metrics??metricSettings}:cfg, rot);
      (controls as any).onBuilderPlaced?.({ id, kind: 'tracker', label: cfg.label ?? 'Tracker', trackerType: cfg.trackerType });
    }
  }

  function builderStartPlacing(cfg: any) {
    builderCancelPlacing();
    placingConfig = cfg;
    builderPlacementActive = true;
    selectBuilderObject(null);

    if (cfg.kind === 'obstacle') {
      ghostMesh = makeObstacleGhost(cfg);
    } else if (cfg.kind === 'tracker') {
      const h = cfg.roofHeight ?? 5;
      // Ghost-Fläche spiegelt Modulanzahl, Raster-Ausrichtung UND Builder-Maßstab
      // wider (Basis-Rack 2x2 = 10x15)
      let [gcols, grows] = getPanelLayout(cfg.panelCount ?? 4);
      if (cfg.panelOrientation === 'landscape') [gcols, grows] = [grows, gcols];
      const gscale = cfg.trackerScale ?? 2;
      const rack=isProject?metricRackSize(gcols,grows,cfg.panelOrientation==='landscape',cfg.metrics??metricSettings):null;
      const gw = rack?.width ?? 10 * (gcols / 2) * gscale;
      const gd = rack?.depth ?? 15 * (grows / 2) * gscale;
      const g = new THREE.Group();
      const panel = new THREE.Mesh(new THREE.BoxGeometry(gw, 0.5, gd), new THREE.MeshBasicMaterial({ color: 0x38bdf8, transparent: true, opacity: 0.65 }));
      panel.position.y = h + 0.25;
      const post = new THREE.Mesh(new THREE.CylinderGeometry(0.5, 0.5, h, 8), new THREE.MeshBasicMaterial({ color: 0xcccccc, transparent: true, opacity: 0.55 }));
      post.position.y = h / 2;
      const edges = new THREE.EdgesGeometry(new THREE.BoxGeometry(gw, 0.5, gd));
      const wire = new THREE.LineSegments(edges, new THREE.LineBasicMaterial({ color: 0x38bdf8 }));
      wire.position.y = h + 0.25;
      g.add(panel, post, wire);
      ghostMesh = g;
    } else if (cfg.kind === 'house') {
      // Always show a box ghost immediately (model loads asynchronously)
      const s = (cfg.scale ?? 5);
      const ghostSize = Array.isArray(cfg.ghostSize) ? cfg.ghostSize : null;
      ghostMesh = ghostSize
        ? makeBoxGhost(ghostSize[0], ghostSize[1], ghostSize[2], 0x38bdf8)
        : makeBoxGhost(s * 6, s * 4, s * 6, 0x38bdf8);
      // Preload model in background so first placement is instant
      resolveHouseModelUrl(cfg).then((path) => loadBuilderModel(path)).catch(() => {});
    }

    if (ghostMesh) {
      ghostMesh.visible = false;
      scene.add(ghostMesh);
      updateGhostScale();
    }

    builderMoveHandler = (e: PointerEvent) => {
      const pt = getBuilderGroundPoint(e.clientX, e.clientY);
      if (!ghostMesh) return;
      if (pt) {
        ghostMesh.visible = true;
        ghostMesh.position.set(pt.x, pt.y, pt.z);
        updateGhostRotation();
        updateGhostScale();
      }
    };

    builderClickHandler = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const dt = performance.now() - downTime;
      if (dt > TIME_THRESH || Math.hypot(e.clientX - downX, e.clientY - downY) > MOVE_THRESH) return;
      finalizePlacement(e.clientX, e.clientY);
    };

    renderer.domElement.addEventListener('pointermove', builderMoveHandler);
    renderer.domElement.addEventListener('pointerup', builderClickHandler);
  }

  function builderCancelPlacing() {
    removeBuilderListeners();
    removeBuilderMoveSelectedListeners(); // falls gerade ein bestehendes Objekt verschoben wird
    if (ghostMesh) { scene.remove(ghostMesh); disposeObjectTree(ghostMesh); ghostMesh = null; }
    placingConfig = null;
    builderPlacementActive = false;
  }

  function enterBuilderMode() {
    builderModeActive = true;
    neighborhoodGroup.visible = false;
    singleGroup.visible = false;
    builderGroup.visible = true;
    builderTerrainMesh.visible = true;
    // Remove mode panels from trackerMap/panelRoots; builder trackers take over
    for (let i = panelRoots.length - 1; i >= 0; i--) {
      if (!panelRoots[i].name.startsWith('b_tracker_')) {
        trackerMap.delete(panelRoots[i].name);
        panelRoots.splice(i, 1);
      }
    }
    // Re-add builder trackers to panelRoots and trackerMap (safe even if already present)
    for (const record of builderPlacedRecords) {
      if (record.kind === 'tracker') {
        if (!panelRoots.includes(record.object)) panelRoots.push(record.object);
        trackerMap.set(record.object.name, record.object as any);
      }
    }
    if (selectedBuilderObjectId) selectBuilderObject(selectedBuilderObjectId);
  }

  function exitBuilderMode() {
    builderModeActive = false;
    builderCancelPlacing();
    selectBuilderObject(null);
    builderGroup.visible = false;
    builderTerrainMesh.visible = false;
    // Remove builder trackers from panelRoots/trackerMap so setHouseMode works cleanly.
    // clearPanels() now also skips b_tracker_ entries, but remove here for correctness.
    for (let i = panelRoots.length - 1; i >= 0; i--) {
      const p = panelRoots[i];
      if (p.name.startsWith('b_tracker_')) {
        trackerMap.delete(p.name);
        panelRoots.splice(i, 1);
      }
    }
    neighborhoodGroup.visible = true;
    singleGroup.visible = houseMode === 'single';
  }

  function clearBuilderScene() {
    // Epoche hochzählen: Async-Platzierungen, die zu einem FRÜHEREN Ladevorgang
    // gehören (GLB-Load war beim Clear noch unterwegs), verfallen. Ohne das
    // stapelte jeder "Modell laden"-Klick während laufender Loads Duplikate
    // unter die Originale (5-8 Kopien übereinander waren die Folge).
    builderLoadEpoch++;
    builderCancelPlacing();
    selectBuilderObject(null);
    for (const record of [...builderPlacedRecords]) {
      deleteBuilderObject(record.id);
    }
    notifyBuilderObjectsChanged();
  }

  (controls as any).enterBuilderMode = enterBuilderMode;
  (controls as any).exitBuilderMode = exitBuilderMode;
  (controls as any).builderStartPlacing = builderStartPlacing;
  (controls as any).builderCancelPlacing = builderCancelPlacing;
  (controls as any).builderSetRotation = (deg: number) => { placementRotationDeg = deg; updateGhostRotation(); };
  (controls as any).builderSetScaleMultiplier = (mult: number) => { placementScaleMultiplier = mult; updateGhostScale(); };
  (controls as any).clearBuilderScene = clearBuilderScene;
  (controls as any).getBuilderObjects = getBuilderObjectSummaries;
  (controls as any).getProjectMetrics = () => ({...metricSettings});
  (controls as any).setProjectMetrics = (value:any) => {metricSettings=projectMetrics(value);};
  (controls as any).applyProjectTrackerMetrics = async(value:any) => {
    metricSettings=projectMetrics(value);
    const snapshot=getBuilderSnapshot().map(item=>item.kind==='tracker'?{...item,cfg:{...item.cfg,metrics:{...metricSettings}}}:item);
    await (controls as any).loadProjectSnapshot(snapshot);
  };
  (controls as any).getProjectObjectDimensions = () => builderPlacedRecords.map((r,index)=>{
    const rotation=r.object.quaternion.clone();r.object.quaternion.identity();r.object.updateWorldMatrix(true,true);
    const size=new THREE.Box3().setFromObject(r.object).getSize(new THREE.Vector3());
    r.object.quaternion.copy(rotation);r.object.updateWorldMatrix(true,true);
    return {id:r.id,index,label:r.label,kind:r.kind,width:size.x/PROJECT_UNITS_PER_METRE,height:size.y/PROJECT_UNITS_PER_METRE,depth:size.z/PROJECT_UNITS_PER_METRE,reference:r.cfg.metricReference};
  });
  (controls as any).getTrackerHeightGuide = (selected = false) => {
    const tracker = selected ? builderPlacedRecords.find(r => r.id === selectedBuilderObjectId && r.kind === 'tracker') : null;
    const metrics = tracker?.cfg.metrics ?? metricSettings;
    return {
      metrics,
      houses: builderPlacedRecords.filter(r => r.kind === 'house' && r.cfg.source === 'site-model').map(r => {
        r.object.updateWorldMatrix(true, true);
        const box = new THREE.Box3().setFromObject(r.object);
        return {id:r.id, height:(box.max.y-box.min.y)/PROJECT_UNITS_PER_METRE,
          top:(box.max.y-builderTerrainMesh.position.y)/PROJECT_UNITS_PER_METRE,
          calibrated:!!r.cfg.metricReference};
      }),
    };
  };
  (controls as any).getProjectTrackerResults = () => builderPlacedRecords
    .filter(record => record.kind === 'tracker')
    .map((record, index) => ({ name: record.label || `Tracker ${index + 1}`, energy_kwh: Number((record.object as any).energyTotal || 0), panel_count: (record.object as any).panelCount ?? 4 }));
  let projectLoading = false;
  (controls as any).isBuilderLoading = () => projectLoading;
  (controls as any).loadProjectSnapshot = async (snapshot: ReturnType<typeof getBuilderSnapshot>) => {
    projectLoading = true;
    try {
      for (let n = 0; n < 100 && snapshot.some(i => i.kind === 'tracker') && !trackerFactoryRef; n++) await new Promise(r => setTimeout(r, 150));
      if (snapshot.some(i => i.kind === 'tracker') && !trackerFactoryRef) throw new Error('Tracker model is not ready');
      // Prepare every house before clearing the existing scene. Failed downloads
      // must not erase a working customer plan or hide the actual error.
      await Promise.all(snapshot.filter(i=>i.kind==='house').map(async item=>{
        const path=await resolveHouseModelUrl(item.cfg);await loadBuilderModel(path);
      }));
      const result = await loadBuilderSnapshot(snapshot);
      if (result.includes(false)) throw new Error('Das Hausmodell konnte nicht geladen werden. Der vorherige Stand bleibt erhalten.');
    } finally { projectLoading = false; }
  };
  (controls as any).fitSelectedBuilding = (reference:any, swap=false) => {
    const record=builderPlacedRecords.find(r=>r.id===selectedBuilderObjectId);
    if(!record||record.kind!=='house')return null;
    const f=reference.footprint,u=PROJECT_UNITS_PER_METRE;
    fitBuilding(record.object,{width:(swap?f.depth_m:f.width_m)*u,height:reference.metres*u,depth:(swap?f.width_m:f.depth_m)*u},f.rotation_deg+(swap?90:0));
    record.rotDeg=f.rotation_deg+(swap?90:0);
    record.cfg={...record.cfg,scale:record.object.scale.x,scale_xyz:record.object.scale.toArray(),metricReference:{...reference,axes_swapped:swap}};
    builderSelectionHelper?.update();notifyBuilderSelectionChanged();return true;
  };
  (controls as any).addProjectNeighbors = (houseId?:string) => {
    const house=builderPlacedRecords.find(r=>r.kind==='house'&&(!houseId||r.id===houseId)&&r.cfg.source==='site-model'&&r.cfg.metricReference?.footprint);
    const ref=house?.cfg.metricReference;
    if(!house||!ref?.footprint?.surroundings?.length)return 0;
    // Replace only generated neighbors. Manually placed obstacles stay untouched.
    for(const old of [...builderPlacedRecords])if(old.cfg.neighbor_reference)deleteBuilderObject(old.id);
    house.object.updateWorldMatrix(true,true);
    const box=new THREE.Box3().setFromObject(house.object),center=box.getCenter(new THREE.Vector3());
    let count=0;
    for(const part of ref.footprint.surroundings){
      const cfg={obstacleType:'footprint',rings_m:part.rings_m,height:ref.metres*PROJECT_UNITS_PER_METRE,neighbor_reference:ref.geodata?.assessment_id||'project',height_source:'Annahme: gleiche Höhe wie Kundenhaus'};
      const obj=buildObstacle(cfg);obj.position.set(center.x,box.min.y,center.z);
      // The source rings use the original footprint axes, regardless of AI-model axis swap.
      const rot=house.rotDeg-(ref.axes_swapped?90:0);obj.rotation.y=THREE.MathUtils.degToRad(rot);
      builderGroup.add(obj);addBuilderOccluders(obj);registerBuilderObject(obj,'obstacle',`Nachbarbebauung ${++count} (geschätzt)`,cfg,rot);
    }
    return count;
  };
  (controls as any).calibrateSelectedBuilder = (axis: 'x'|'y'|'z', target: number, reference?: any) => {
    const index = builderPlacedRecords.findIndex(r => r.id === selectedBuilderObjectId);
    const record = builderPlacedRecords[index];
    if (!record || record.kind === 'tracker' || !['x','y','z'].includes(axis) || !Number.isFinite(target) || target <= 0) return null;
    const object = record.object;
    const rotation = object.quaternion.clone();
    object.quaternion.identity(); object.updateWorldMatrix(true, true);
    const size = new THREE.Box3().setFromObject(object).getSize(new THREE.Vector3());
    object.quaternion.copy(rotation); object.updateWorldMatrix(true, true);
    const previous = size[axis];
    if (!Number.isFinite(previous) || previous <= 0) return null;
    const oldBottom = new THREE.Box3().setFromObject(object).min.y;
    const scale = object.scale.x * target / previous;
    builderSetSelectedScale(scale);
    object.updateWorldMatrix(true, true);
    object.position.y += oldBottom - new THREE.Box3().setFromObject(object).min.y;
    record.cfg.metricReference=reference;
    builderSelectionHelper?.update(); notifyBuilderSelectionChanged();
    return { objectIndex: index, label: record.label, previous_scene_length: previous, new_scene_length: target, scale };
  };
  (controls as any).getBuilderSnapshot = getBuilderSnapshot;
  (controls as any).loadBuilderSnapshot = loadBuilderSnapshot;
  (controls as any).selectBuilderObject = selectBuilderObject;
  (controls as any).getSelectedBuilderTransform = getSelectedBuilderTransform;
  (controls as any).builderSetSelectedRotation = builderSetSelectedRotation;
  (controls as any).builderSetSelectedScale = builderSetSelectedScale;
  (controls as any).builderSetSelectedHeight = builderSetSelectedHeight;
  (controls as any).builderSetSelectedMounting = builderSetSelectedMounting;
  (controls as any).setSiteModelUrlProvider = setSiteModelUrlProvider;

  /** Kamera-Sprung zu einem Tracker (Klick auf eine Zeile der Bestenliste):
   * behält die aktuelle Blickrichtung bei, stellt die Kamera in ~90 Einheiten
   * Abstand leicht erhöht davor und blendet die Info-Karte des Trackers ein. */
  (controls as any).focusTracker = (name: string) => {
    const panel = trackerMap.get(name);
    if (!panel) return;
    stopAmbientLoop();
    const target = new THREE.Vector3();
    (panel as any).getWorldPosition(target);
    const dir = camera.position.clone().sub(controls.target);
    if (dir.lengthSq() < 1) dir.set(60, 50, 60);
    dir.normalize().multiplyScalar(90);
    dir.y = Math.max(dir.y, 40);
    camera.position.copy(target).add(dir);
    controls.target.copy(target);
    controls.update();
    panelInfoMap.get(name)?.setVisible(true);
  };
  (controls as any).builderStartMoveSelected = builderStartMoveSelected;
  (controls as any).builderDuplicateSelected = builderDuplicateSelected;
  (controls as any).deleteBuilderObject = deleteBuilderObject;
  (controls as any).getBuilderOccluderMeshes = () => builderOccluderMeshes;
  (controls as any).setBuilderGroundY = (y: number) => { builderTerrainMesh.position.y = y; notifyBuilderSelectionChanged(); };

  // ============== Schatten-Wand ==============
  // Betonwand südlich der 30°-Panels — schirmt dual_nord, dual_west u.a. ab
  // Wand südlich (z=+5) der 30°-Panel-Gruppe (Panels bei z=-8 bis -65).
  // Sonne bei Mittagszeit in positiver Z-Richtung → Wand zwischen Sonne und Panels.
  // Breite 160 deckt x:-235 bis -75 ab (dual_nord -150, dual_west -205, dual_east -185).
  // Höhe 65 damit der Sonnenstrahl bei ~15-20° Elevation abgefangen wird.
  const wallGeo  = new THREE.BoxGeometry(160, 65, 14);
  const wallMat  = new THREE.MeshStandardMaterial({
    color: 0x8a8a80,
    roughness: 0.90,
    metalness: 0.02,
  });
  const wallMesh = new THREE.Mesh(wallGeo, wallMat);
  wallMesh.name = 'shadow_wall';
  wallMesh.position.set(-155, 32, 5);   // Zentrum bei y=32 → Wand y:0 bis 65
  wallMesh.castShadow    = true;
  wallMesh.receiveShadow = true;
  scene.add(wallMesh);
  if (wallMeshRef) wallMeshRef.current = wallMesh;

  // ============== Resize/FS & Loop ==============
  const resizeNow = () => {
    const { w, h } = getSize();
    camera.aspect = w / h;
    camera.updateProjectionMatrix();
    renderer.setSize(w, h, false);
    fitAuroraToCamera();
  };
  const ro = mountRef.current ? new ResizeObserver(() => resizeNow()) : null;
  if (mountRef.current && ro) ro.observe(mountRef.current);
  const onFs = () => resizeNow();
  window.addEventListener('resize', resizeNow);
  document.addEventListener('fullscreenchange', onFs);

  let animationFrameId: number;
  const animate = () => {
    animationFrameId = requestAnimationFrame(animate);
    const now = skyClock.getElapsedTime();
    const prev = (animate as any)._prev ?? now;
    const dt = Math.min(0.05, (now - prev) || 0.016);
    (animate as any)._prev = now;

    if (galaxySphere.visible) galaxySphere.rotation.y += 0.02 * dt;
    auroraUniforms.time.value = now;
    starMat.uniforms.time.value = now;
    skyLayer.position.copy(camera.position);

    if ((currentPreset === 'night' || currentPreset === 'galaxy') && Math.random() < 0.2) spawnMeteor();

    for (let i = meteors.length - 1; i >= 0; i--) {
      const m = meteors[i];
      m.angle += m.omega * dt; m.life += dt;
      const p0 = m.mesh.position.clone().normalize();
      const p = p0.applyAxisAngle(m.axis, m.omega * dt).setLength(SKY_R);
      m.mesh.position.copy(p);
      const tang = new THREE.Vector3().crossVectors(p, m.axis).normalize();
      m.mesh.quaternion.setFromUnitVectors(new THREE.Vector3(1, 0, 0), tang);
      const a = 1 - (m.life / m.maxLife);
      (m.mesh.material as THREE.MeshBasicMaterial).opacity = Math.max(0, a * a);
      if (m.light) {
          m.light.position.copy(p);
          const fade = Math.max(0, a * a);
          m.light.intensity = 1.2 * fade;
        }

        if (m.life >= m.maxLife) {
          skyLayer.remove(m.mesh);
          m.mesh.geometry.dispose();
          (m.mesh.material as THREE.Material).dispose();

          if (m.light) { skyLayer.remove(m.light); m.light = null; }

          meteors.splice(i, 1);
        }
      }

    // Intro
    let runControlsUpdate = true;
    if (introActive) {
      runControlsUpdate = false;
      if (userInteracted) {
        finishIntro(true);
      } else {
        const t = Math.min((performance.now() - clockStart) / introDurationMs, 1);
        const e = easeInOut(t);
        if (introPathCurve && introPathEndLook) {
          // Dorf-Flug: entlang der Kurve (durchs Tor), Blick voraus — gegen
          // Ende weich auf das Endziel (Marktplatz/Brunnen) schwenken.
          introPathCurve.getPointAt(e, camera.position);
          const ahead = introPathCurve.getPointAt(Math.min(e + 0.06, 1), new THREE.Vector3());
          const blend = THREE.MathUtils.smoothstep(e, 0.75, 1);
          tmpLook.copy(ahead).lerp(introPathEndLook, blend);
          camera.lookAt(tmpLook);
        } else {
          camera.position.lerpVectors(START_POS, END_POS, e);
          tmpLook.lerpVectors(START_LOOK, END_LOOK, e);
          camera.lookAt(tmpLook);
          camera.fov = START_FOV + (END_FOV - START_FOV) * e;
          camera.updateProjectionMatrix();
        }
        if (t >= 1) {
          finishIntro(false);
          runControlsUpdate = false;
        }
      }
    } else if (ambientLoopActive) {
      runControlsUpdate = false;
      const t = ((performance.now() - ambientClockStart) / ambientDurationMs) % 1;
      // Loop-Wrap: Pfad neu berechnen — asynchron nachgeladene Häuser vergrößern
      // die Bbox; der Bbox-Orbit wächst mit, der Straßen-Pfad hebt seine
      // Mindesthöhe auf (höchstes Gebäude + 25) an.
      if (ambientFocusBuilder && t < ambientLastT) {
        const c = ambientCustomPath ? buildCustomAmbientCurve() : computeBuilderAmbientCurve();
        if (c) ambientCurve = c;
      }
      ambientLastT = t;
      ambientCurve.getPointAt(t, ambientPos);
      ambientCurve.getPointAt((t + 0.012) % 1, ambientAhead);
      ambientLook.subVectors(ambientAhead, ambientPos);
      if (ambientLook.lengthSq() < 1e-6) ambientLook.set(0, 0, -1);
      else ambientLook.normalize();
      ambientLook.multiplyScalar(70).add(ambientPos);
      ambientLook.y -= 45;
      camera.position.copy(ambientPos);
      camera.lookAt(ambientLook);
    }
    if (runControlsUpdate) controls.update();

    if (builderSelectionHelper) builderSelectionHelper.update();
    if (!renderPausedRef?.current) renderer.render(scene, camera);
  };

  if (isProject) { enterBuilderMode(); stopAmbientLoop(); }
  updateSunPosition();
  animate();

  // === Cleanup
  return () => {
    disposed = true;
    cancelAnimationFrame(animationFrameId);
    wallGeo.dispose();
    wallMat.dispose();
    if (wallMeshRef) wallMeshRef.current = null;
    builderTerrainGeo.dispose();
    builderTerrainMat.dispose();
    clearBuilderScene();
    window.removeEventListener('resize', resizeNow);
    document.removeEventListener('fullscreenchange', onFs);
    ro?.disconnect();

    renderer.domElement.removeEventListener('pointerdown', onPointerDown);
    renderer.domElement.removeEventListener('pointerup', onPointerUp);
    renderer.domElement.removeEventListener('contextmenu', onContextMenu);
    removeBuilderMoveSelectedListeners();
    window.removeEventListener('keydown', onEsc);

    renderer.domElement.removeEventListener('pointerdown', markInteracted);
    renderer.domElement.removeEventListener('wheel', markInteracted);

    if (mountRef.current?.firstChild) {
      mountRef.current.removeChild(mountRef.current.firstChild as any);
    }

    disposePanelInfos();
    disposeObjectTree(scene);
    disposeMaterial(meteorBaseMat);
    disposeTexture(meteorTex);
    controls.dispose();
    scene.clear();
    renderer.dispose();
  };

};
